import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTheme } from '../theme/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import {
  Modal,
  ModalSidebar,
  ModalMainContent,
  SidebarNav,
  ToggleRow,
  Button,
  ModalInput,
  Badge,
  Checkbox,
  Select,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../ui';
import { useToast } from '../ui/Toaster';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { displayPhoneKE, formatPhoneKEForInput, normalizePhoneKE } from '../../utils/formatters';
import {
  landlordBankService,
  BankCode,
  BankAccount,
} from '../../services/landlordBankService';
import {
  payheroChannelService,
  PayHeroChannel,
  PayHeroChannelData,
} from '../../services/payheroChannelService';
import {
  organizationService,
  OrganizationType,
  PropertyOwner,
  PropertyOwnerType,
} from '../../services/organization.service';
import {
  consumeSettingsReturnToAddProperty,
  dispatchResumeAddProperty,
} from '../../utils/settingsReturnFlow';
import { useCloseOnRouteChange } from '../../hooks/useCloseOnRouteChange';
import {
  Bell,
  SlidersHorizontal,
  ShieldCheck,
  UserCircle2,
  Plus,
  Trash2,
  Eye,
  Save,
  Edit3,
  Sun,
  Moon,
  Activity,
  Users,
  Download,
  Check,
  AlertTriangle,
  Building2,
  Star,
  Wallet,
  TrendingUp,
  PieChart,
  CreditCard,
  FileText,
  Zap,
  KeyRound,
  Hash,
  X,
  Menu,
} from 'lucide-react';
import { TeamManagement } from '../team/TeamManagement';
import {
  SettingsSectionSkeleton,
  SettingsInlineSkeleton,
  SettingsChannelGridSkeleton,
} from '../ui/PageRouteSkeleton';
import { apiService } from '../../services/api';
import { disbursementService, PayoutHistory } from '../../services/disbursementService';
import { billingService, type BillingStatusResponse, type BillingInvoiceItem } from '../../services/billingService';

// ─── Navigation groups (analytics & properties removed – covered on main dashboard) ──
const NAV_GROUPS = [
  {
    label: 'Profile',
    items: [
      { id: 'account', label: 'Account', icon: UserCircle2, desc: 'Profile & organization' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { id: 'billing', label: 'Billing', icon: CreditCard, desc: 'Subscription & invoices' },
      { id: 'payment-channels', label: 'Payment Channels', icon: Zap, desc: 'PayHero channels for rent collection' },
    ],
  },
  {
    label: 'Manage',
    items: [
      { id: 'team', label: 'Team', icon: Users, desc: 'Add and manage team members' },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'notifications', label: 'Notifications', icon: Bell, desc: 'Email, SMS & push alerts' },
      { id: 'security', label: 'Security', icon: ShieldCheck, desc: 'Password & account security' },
      { id: 'preferences', label: 'Preferences', icon: SlidersHorizontal, desc: 'Theme & display' },
      { id: 'activity', label: 'Activity Log', icon: Activity, desc: 'Recent account activity' },
    ],
  },
];

// ─── Styled shell ─────────────────────────────────────────────────────────────
const SOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.48);
  backdrop-filter: blur(4px);
  z-index: 999;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
  animation: sfadeIn 0.2s ease;
  @keyframes sfadeIn { from { opacity: 0 } to { opacity: 1 } }
`;

const SPanel = styled.div`
  --foreground: ${p => p.theme.foreground};
  --muted-foreground: ${p => p.theme.mutedForeground};
  --border: ${p => p.theme.border};
  --background: ${p => p.theme.background};
  --text: ${p => p.theme.foreground};
  --text-muted: ${p => p.theme.mutedForeground};
  --bg-muted: ${p => p.theme.muted};
  --bg: ${p => p.theme.card};
  --primary: ${p => p.theme.primary};
  --primary-foreground: ${p => p.theme.primaryForeground};
  width: min(940px, 100vw);
  height: 100vh;
  background: ${p => p.theme.background};
  color: ${p => p.theme.foreground};
  display: flex;
  overflow: hidden;
  box-shadow: -8px 0 48px rgba(0, 0, 0, 0.2);
  animation: spSlideIn 0.32s cubic-bezier(0.16, 1, 0.3, 1);
  @keyframes spSlideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }

  @media (max-width: 768px) {
    width: 100vw;
  }
`;

const SSidebar = styled.nav<{ $mobileOpen?: boolean }>`
  width: 240px;
  min-width: 240px;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.muted};
  overflow: hidden;
  position: relative;
  z-index: 50;

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    top: 0;
    width: 100%;
    max-width: 280px;
    height: 100vh;
    border-right: none;
    transform: ${p => p.$mobileOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

const SSidebarTop = styled.div`
  padding: 18px 16px 14px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px 14px 12px;
  }
`;

const SBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`;

const SBrandIcon = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: ${p => p.theme.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.primaryForeground};
  flex-shrink: 0;
`;

const SCloseBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.border};
  background: transparent;
  color: ${p => p.theme.mutedForeground};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
  &:hover {
    background: ${p => p.theme.muted};
    color: ${p => p.theme.foreground};
  }
`;

const SMainCloseBtn = styled(SCloseBtn)`
  width: 36px;
  height: 36px;
  margin-left: auto;
`;

const SMobileMenuToggle = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.border};
  background: transparent;
  color: ${p => p.theme.mutedForeground};
  cursor: pointer;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, color 0.15s;

  @media (max-width: 768px) {
    display: flex;
    margin-right: 8px;
  }

  &:hover {
    background: ${p => p.theme.muted};
    color: ${p => p.theme.foreground};
  }
`;

const SMobileOverlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${p => p.$open ? 'block' : 'none'};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 50;
  }
`;

const SNavScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 4px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const SNavGroup = styled.div`
  padding: 4px 0 8px;
  &:not(:last-child) { border-bottom: 1px solid var(--border); }
`;

const SNavGroupLabel = styled.div`
  padding: 8px 16px 5px;
  font-size: 10px;
  font-weight: 700;
  color: var(--muted-foreground);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.65;
`;

const SNavBtn = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 16px;
  border: none;
  outline: none;
  box-shadow: none;
  border-radius: 8px;
  /* Sidebar is theme.muted — active must contrast (not muted-on-muted) */
  background: ${p => (p.$active ? p.theme.card : 'transparent')};
  color: ${p => p.theme.foreground};
  font-size: 13.5px;
  font-weight: ${p => (p.$active ? '600' : '400')};
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, color 0.12s;
  line-height: 1.4;
  &:hover {
    background: ${p => p.theme.card};
    color: ${p => p.theme.foreground};
  }
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
    border: none;
  }
`;

const SNavBtnIcon = styled.span<{ $active: boolean }>`
  width: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${p => (p.$active ? 1 : 0.6)};
  flex-shrink: 0;
`;

const SSidebarFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const SAvatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${p => p.theme.gradientPrimary};
  color: ${p => p.theme.primaryForeground};
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SMain = styled.main`
  flex: 1;
  min-width: 0;
  height: 100%;
  background: transparent;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SMainHeader = styled.div`
  padding: 18px 28px 16px;
  border-bottom: 1px solid ${p => p.theme.border};
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  background: ${p => p.theme.muted};

  @media (max-width: 768px) {
    padding: 14px 16px 12px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    padding: 12px 14px 10px;
    gap: 8px;
  }
`;

const SMainHeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${p => p.theme.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.primaryForeground};
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;
  }
`;

const SMainHeaderBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;

  @media (max-width: 640px) {
    gap: 8px;
  }
`;

const SMainHeaderText = styled.div`
  min-width: 0;
`;

const SMainScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px 40px;
  background: transparent;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

  @media (max-width: 768px) {
    padding: 20px 20px 32px;
  }

  @media (max-width: 480px) {
    padding: 16px 14px 28px;
  }
`;
// ─────────────────────────────────────────────────────────────────────────────

// Types
interface LandlordSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, open directly to this section (e.g. 'billing' when redirected after gate 403). */
  initialSection?: string;
}

// Note: Real landlord data will come from authenticated user via useAuth hook

// Component
const LandlordSettingsModal: React.FC<LandlordSettingsModalProps> = ({ isOpen, onClose, initialSection }) => {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const toast = useToast();
  const [activeSection, setActiveSection] = useState('account');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const settingsSectionKey = useMemo(
    () => `landlordSettings:activeSection:${user?.id ?? 'anon'}`,
    [user?.id],
  );
  /** Only true when user clicks sidebar nav (not restore from localStorage / initialSection). */
  const userNavigatedSectionRef = useRef(false);
  const [sectionLoading, setSectionLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initialSection) {
      setActiveSection(initialSection === 'payouts' ? 'billing' : initialSection);
      return;
    }
    try {
      const saved = localStorage.getItem(settingsSectionKey);
      if (saved && NAV_GROUPS.flatMap(g => g.items).some(i => i.id === saved)) {
        setActiveSection(saved);
      }
    } catch {
      /* ignore */
    }
  }, [isOpen, initialSection, settingsSectionKey]);

  useEffect(() => {
    if (!isOpen) return;
    try {
      localStorage.setItem(settingsSectionKey, activeSection);
    } catch {
      /* ignore */
    }
  }, [activeSection, isOpen, settingsSectionKey]);

  useEffect(() => {
    if (!isOpen || !userNavigatedSectionRef.current) return;
    userNavigatedSectionRef.current = false;
    setSectionLoading(true);
    const t = window.setTimeout(() => setSectionLoading(false), 170);
    return () => window.clearTimeout(t);
  }, [activeSection, isOpen]);

  // Close sidebar when section changes on mobile
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  }, [activeSection]);

  useEffect(() => {
    if (!isOpen) {
      setSidebarOpen(false);
    }
  }, [isOpen]);
  const [showDeleteBankConfirm, setShowDeleteBankConfirm] = useState(false);
  const [showSetPrimaryConfirm, setShowSetPrimaryConfirm] = useState(false);
  const [bankAccountToDelete, setBankAccountToDelete] = useState<string | null>(null);
  const [bankAccountToSetPrimary, setBankAccountToSetPrimary] = useState<string | null>(null);

  // User/landlord ID state - must be declared early as it's used in useEffect hooks
  const [landlordId, setLandlordId] = useState<string | null>(null);

  const [analyticsData, setAnalyticsData] = useState<{
    monthlyRevenue: number;
    activeTenants: number;
    occupancyRate: number;
    revenueChange: number;
    loading: boolean;
  }>({
    monthlyRevenue: 0,
    activeTenants: 0,
    occupancyRate: 0,
    revenueChange: 0,
    loading: true
  });

  const [billingData, setBillingData] = useState<{
    totalUnits: number;
    bandLabel?: string;
    estimate: { amount: number | null; isCustom: boolean; breakdown: { units: number; rate: number; subtotal: number }[]; currency: string };
  } | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [showBillingBreakdown, setShowBillingBreakdown] = useState(false);

  const [billingStatus, setBillingStatus] = useState<BillingStatusResponse | null>(null);
  const [billingStatusLoading, setBillingStatusLoading] = useState(false);
  const [billingInvoices, setBillingInvoices] = useState<BillingInvoiceItem[]>([]);
  const [billingActivating, setBillingActivating] = useState(false);
  const [billingRenewalPhone, setBillingRenewalPhone] = useState('');
  const [billingRenewalInvoiceId, setBillingRenewalInvoiceId] = useState<string | null>(null);
  const [billingRenewalTransactionId, setBillingRenewalTransactionId] = useState<string | null>(null);
  const [billingRenewalSubmitting, setBillingRenewalSubmitting] = useState(false);
  const [billingRenewalChecking, setBillingRenewalChecking] = useState(false);
  const [billingRenewalError, setBillingRenewalError] = useState<string | null>(null);
  const [billingRenewalMessage, setBillingRenewalMessage] = useState<string | null>(null);

  // API user profile (from /auth/me) - source of truth for phone, displayName so they persist
  const [apiUser, setApiUser] = useState<{ phone?: string | null; displayName?: string | null; email?: string } | null>(null);

  // Company / document defaults (currency, country) — org profile is canonical for name/type
  const [companyName, setCompanyName] = useState('');
  const [currency, setCurrency] = useState('KES');
  const [accountType, setAccountType] = useState<'LANDLORD' | 'MANAGEMENT_FIRM'>('LANDLORD');
  const [country, setCountry] = useState('KE');
  const [companyDetailsLoading, setCompanyDetailsLoading] = useState(false);
  const [companyDetailsSaving, setCompanyDetailsSaving] = useState(false);
  const [companyDetailsError, setCompanyDetailsError] = useState<string | null>(null);
  const [companyDetailsSuccess, setCompanyDetailsSuccess] = useState(false);

  const [accountTab, setAccountTab] = useState<'profile' | 'organization'>('profile');
  const [orgForm, setOrgForm] = useState({
    name: '',
    type: 'INDIVIDUAL_LANDLORD' as OrganizationType,
    email: '',
    phone: '',
    website: '',
    primaryCity: '',
  });
  const [orgMeta, setOrgMeta] = useState<{ id: string; slug: string; status: string; onboardingCompletedAt: string | null } | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);
  const [orgSuccess, setOrgSuccess] = useState(false);
  const [propertyOwners, setPropertyOwners] = useState<PropertyOwner[]>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownerFormMode, setOwnerFormMode] = useState<'closed' | 'create' | 'edit'>('closed');
  const [editingOwnerId, setEditingOwnerId] = useState<string | null>(null);
  const [ownerForm, setOwnerForm] = useState({
    name: '',
    type: 'PERSON' as PropertyOwnerType,
    email: '',
    phone: '',
    notes: '',
    isDefault: false,
  });
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [ownerToDelete, setOwnerToDelete] = useState<PropertyOwner | null>(null);
  const [ownerDeleting, setOwnerDeleting] = useState(false);

  // Helper function to get user display name (prefer API, then Firebase)
  const getUserDisplayName = (): string => {
    const name = apiUser?.displayName || (user as any)?.displayName || (user as any)?.email?.split('@')[0] || 'User';
    return name || 'User';
  };

  // Helper function to split name into first and last (from API or Firebase)
  const splitUserName = () => {
    const displayName = apiUser?.displayName ?? (user as any)?.displayName ?? '';
    const fallback = (user as any)?.email?.split('@')[0] ?? '';
    const raw = (displayName || fallback || '').trim();
    const names = raw ? raw.split(/\s+/) : [];
    return {
      firstName: names[0] || '',
      lastName: names.slice(1).join(' ') || ''
    };
  };

  // Account settings state - initialized from API user (phone, displayName) then Firebase (email)
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileData, setProfileData] = useState(() => {
    const { firstName, lastName } = splitUserName();
    return {
      firstName,
      lastName,
      email: (user as any)?.email || '',
      phone: (user as any)?.phone ?? (user as any)?.phoneNumber ?? '',
    };
  });

  // Update profile data when API user or Firebase user changes (API is source of truth for phone/name).
  // Skip overwriting when user is editing so we don't wipe in-progress phone/name input.
  useEffect(() => {
    if (isEditingProfile) return;
    const { firstName, lastName } = splitUserName();
    const email = apiUser?.email ?? (user as any)?.email ?? '';
    const phone = apiUser?.phone ?? (user as any)?.phoneNumber ?? '';
    setProfileData(prev => ({
      ...prev,
      firstName: firstName || prev.firstName,
      lastName: lastName || prev.lastName,
      email: email || prev.email,
      phone: phone ?? prev.phone ?? '',
    }));
  }, [apiUser, user, isEditingProfile]);

  // Fetch backend user when Account section is shown so profile (phone, displayName) persists after refresh
  useEffect(() => {
    if (!isOpen || !['account', 'billing'].includes(activeSection) || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getCurrentUser();
        const u = res.data?.user;
        if (!cancelled && u) {
          setApiUser({
            phone: u.phone ?? null,
            displayName: u.displayName ?? null,
            email: u.email ?? ''
          });
        }
      } catch (e) {
        if (!cancelled && process.env.NODE_ENV === 'development') {
          console.warn('Failed to load profile for account section:', e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, activeSection, user]);

  useEffect(() => {
    if (activeSection !== 'billing' || !isOpen) return;
    const nextPhone = apiUser?.phone ?? profileData.phone ?? (user as any)?.phone ?? '';
    if (nextPhone && !billingRenewalPhone) {
      setBillingRenewalPhone(displayPhoneKE(nextPhone));
    }
  }, [activeSection, isOpen, apiUser, profileData.phone, user, billingRenewalPhone]);

  // Fetch analytics data when analytics section is active
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (activeSection === 'analytics' && user) {
        try {
          setAnalyticsData(prev => ({ ...prev, loading: true }));

          // Fetch dashboard stats
          const statsResponse = await apiService.getDashboardStats();
          const stats = statsResponse.data.stats;

          // Fetch payment analytics for revenue trends
          const analyticsResponse = await apiService.getPaymentAnalytics();
          const analytics = analyticsResponse.data;

          // Calculate revenue change from monthly trends
          let revenueChange = 0;
          if (analytics && analytics.monthlyTrends && Array.isArray(analytics.monthlyTrends) && analytics.monthlyTrends.length >= 2) {
            const sortedTrends = [...analytics.monthlyTrends].sort((a, b) => a.month.localeCompare(b.month));
            const currentMonth = sortedTrends[sortedTrends.length - 1];
            const previousMonth = sortedTrends[sortedTrends.length - 2];
            if (previousMonth && previousMonth.amount > 0 && currentMonth) {
              revenueChange = ((currentMonth.amount - previousMonth.amount) / previousMonth.amount) * 100;
            }
          }

          // Calculate occupancy rate
          const occupancyRate = stats && stats.totalUnits > 0
            ? Math.round((stats.occupiedUnits / stats.totalUnits) * 100)
            : 0;

          setAnalyticsData({
            monthlyRevenue: (stats && stats.monthlyRevenue) ? stats.monthlyRevenue : 0,
            activeTenants: (stats && stats.totalTenants) ? stats.totalTenants : 0,
            occupancyRate,
            revenueChange: Math.round(revenueChange * 100) / 100,
            loading: false
          });
        } catch (error) {
          console.error('Error fetching analytics:', error);
          setAnalyticsData(prev => ({ ...prev, loading: false }));
        }
      }
    };

    fetchAnalytics();
  }, [activeSection, user]);

  // Fetch billing/plan when Account section is active
  useEffect(() => {
    const fetchBilling = async () => {
      if (activeSection === 'account' && isOpen && user) {
        try {
          setBillingLoading(true);
          const res = await apiService.get('/landlord-settings/billing');
          setBillingData(res.data);
        } catch (e) {
          console.error('Error fetching billing:', e);
          setBillingData(null);
        } finally {
          setBillingLoading(false);
        }
      }
    };
    fetchBilling();
  }, [activeSection, isOpen, user]);

  const refreshBillingEngineData = useCallback(async () => {
    if (!user) return;
    try {
      setBillingStatusLoading(true);
      const [statusRes, invoicesRes] = await Promise.all([
        billingService.getStatus().catch(() => null),
        billingService.getInvoices(20).catch(() => ({ invoices: [] }))
      ]);
      setBillingStatus(statusRes ?? null);
      setBillingInvoices(invoicesRes?.invoices ?? []);
    } catch (e) {
      console.error('Error fetching billing status:', e);
      setBillingStatus(null);
      setBillingInvoices([]);
    } finally {
      setBillingStatusLoading(false);
    }
  }, [user]);

  // Fetch billing engine status and invoices when Billing tab is active
  useEffect(() => {
    if (activeSection === 'billing' && isOpen && user) {
      void refreshBillingEngineData();
    }
  }, [activeSection, isOpen, user, refreshBillingEngineData]);

  const syncBillingRenewalStatus = useCallback(async (
    invoiceId: string,
    transactionId?: string | null,
    showPendingToast: boolean = false,
  ) => {
    try {
      setBillingRenewalChecking(true);
      const res = await billingService.getRenewalStatus(invoiceId, transactionId || undefined);
      const status = res.data.status;

      if (status === 'PAID') {
        setBillingRenewalInvoiceId(null);
        setBillingRenewalTransactionId(null);
        setBillingRenewalError(null);
        setBillingRenewalMessage('Payment verified. Your subscription has been renewed.');
        await refreshBillingEngineData();
        toast.success('Subscription renewed', {
          description: 'Payment verified successfully. Your account is active again.',
        });
      } else if (status === 'FAILED') {
        setBillingRenewalInvoiceId(null);
        setBillingRenewalTransactionId(null);
        setBillingRenewalMessage(null);
        setBillingRenewalError('Billing payment failed or was cancelled. Please try again.');
        await refreshBillingEngineData();
        toast.error('Billing payment failed', {
          description: 'The renewal payment did not complete. Please retry the STK push.',
        });
      } else {
        setBillingRenewalError(null);
        setBillingRenewalMessage('STK push sent. Complete the payment on your phone, then wait for automatic verification.');
        if (showPendingToast) {
          toast.info('Still waiting for payment', {
            description: 'Complete the STK prompt on your phone. We will unlock billing as soon as it is verified.',
          });
        }
      }

      return res.data;
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Could not verify billing payment status.';
      setBillingRenewalError(message);
      return null;
    } finally {
      setBillingRenewalChecking(false);
    }
  }, [refreshBillingEngineData, toast]);

  useEffect(() => {
    if (activeSection !== 'billing' || !isOpen || !billingRenewalInvoiceId) return;

    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (cancelled || !billingRenewalInvoiceId) return;
      attempts += 1;
      const result = await syncBillingRenewalStatus(billingRenewalInvoiceId, billingRenewalTransactionId, false);
      if (cancelled) return;

      if (result?.status === 'PENDING' && attempts < 45) {
        timer = setTimeout(poll, 4000);
      }
    };

    timer = setTimeout(poll, 4000);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [activeSection, isOpen, billingRenewalInvoiceId, billingRenewalTransactionId, syncBillingRenewalStatus]);

  const handleBillingRenewal = async () => {
    const normalizedPhone = normalizePhoneKE(billingRenewalPhone);

    if (!/^254\d{9}$/.test(normalizedPhone)) {
      setBillingRenewalError('Please enter a valid Kenyan phone number in the format 2547XXXXXXXX.');
      return;
    }

    try {
      setBillingRenewalSubmitting(true);
      setBillingRenewalError(null);
      setBillingRenewalMessage(null);

      const res = await billingService.initiateRenewal(normalizedPhone, '1.0');
      setBillingRenewalPhone(res.data.phone);
      setBillingRenewalInvoiceId(res.data.invoiceId);
      setBillingRenewalTransactionId(res.data.transactionId);
      setBillingRenewalMessage(`STK push sent to ${res.data.phone}. Enter your M-Pesa PIN on the phone to complete the renewal payment.`);

      toast.success('STK push sent', {
        description: `A billing payment prompt has been sent to ${res.data.phone}.`,
      });
    } catch (error: any) {
      const message = error?.response?.data?.error || error?.message || 'Failed to start the billing payment.';
      setBillingRenewalError(message);
      toast.error('Could not start billing payment', { description: message });
    } finally {
      setBillingRenewalSubmitting(false);
    }
  };

  // Fetch document defaults when Account section is active
  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (activeSection === 'account' && isOpen && user) {
        try {
          setCompanyDetailsLoading(true);
          setCompanyDetailsError(null);
          const res = await apiService.get('/landlord-settings/company-details');
          setCompanyName(res.data?.companyName ?? '');
          setCurrency(res.data?.currency ?? 'KES');
          setAccountType(res.data?.accountType === 'MANAGEMENT_FIRM' ? 'MANAGEMENT_FIRM' : 'LANDLORD');
          setCountry((res.data?.country ?? 'KE') as string);
        } catch (e) {
          console.error('Error fetching company details:', e);
          setCompanyDetailsError('Failed to load document defaults.');
        } finally {
          setCompanyDetailsLoading(false);
        }
      }
    };
    fetchCompanyDetails();
  }, [activeSection, isOpen, user]);

  useEffect(() => {
    if (activeSection !== 'account' || !isOpen || !user || accountTab !== 'organization') return;
    let cancelled = false;
    (async () => {
      setOrgLoading(true);
      setOrgError(null);
      try {
        const { organization } = await organizationService.getMine();
        if (cancelled) return;
        setOrgMeta({
          id: organization.id,
          slug: organization.slug,
          status: organization.status,
          onboardingCompletedAt: organization.onboardingCompletedAt ?? null,
        });
        setOrgForm({
          name: organization.name || '',
          type: organization.type || 'INDIVIDUAL_LANDLORD',
          email: organization.email || '',
          phone: organization.phone || '',
          website: organization.website || '',
          primaryCity: organization.primaryCity || '',
        });
        setOwnersLoading(true);
        try {
          const owners = await organizationService.listPropertyOwners();
          if (!cancelled) setPropertyOwners(owners);
        } catch {
          if (!cancelled) setPropertyOwners([]);
        } finally {
          if (!cancelled) setOwnersLoading(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setOrgError(e?.response?.data?.error || e?.message || 'Failed to load organization');
        }
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection, isOpen, user, accountTab]);

  // Property owners for channel tagging (Payment Channels section)
  useEffect(() => {
    if (activeSection !== 'payment-channels' || !isOpen || !user) return;
    if (propertyOwners.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const owners = await organizationService.listPropertyOwners();
        if (!cancelled) setPropertyOwners(owners);
      } catch {
        if (!cancelled) setPropertyOwners([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSection, isOpen, user, propertyOwners.length]);

  // Fetch properties when properties section is active
  useEffect(() => {
    const fetchProperties = async () => {
      if (activeSection === 'properties' && landlordId) {
        try {
          setPropertiesLoading(true);
          const response = await apiService.getProperties();
          const propertiesData = response.data?.properties || response.data || [];
          setProperties(propertiesData.map((prop: any) => ({
            id: prop.id,
            name: prop.name,
            totalUnits: prop.totalUnits || 0,
            location: prop.location || '',
            status: 'active',
            payHeroChannelId: prop.payHeroChannelId || prop.payheroChannelId || null,
          })));
        } catch (error) {
          console.error('Error fetching properties:', error);
          setProperties([]);
        } finally {
          setPropertiesLoading(false);
        }
      }
    };

    fetchProperties();
  }, [activeSection, user]);

  // Fetch activity logs when activity section is active
  useEffect(() => {
    const fetchActivityLogs = async () => {
      if (activeSection === 'activity' && landlordId) {
        try {
          setActivityLogsLoading(true);
          const response = await apiService.getAuditLogs({ limit: 50 });
          // Handle both response formats: { data: [...] } or { success: true, data: [...] }
          const logs = response.data?.data || response.data || [];
          if (Array.isArray(logs)) {
            setActivityLogs(logs);
          } else {
            setActivityLogs([]);
          }
        } catch (error) {
          console.error('Error fetching activity logs:', error);
          setActivityLogs([]);
        } finally {
          setActivityLogsLoading(false);
        }
      }
    };

    fetchActivityLogs();
  }, [activeSection, landlordId]);

  // Fetch notification preferences when notifications section is active
  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      if (activeSection === 'notifications' && landlordId) {
        try {
          setNotificationsLoading(true);
          const response = await apiService.getNotificationPreferences();
          if (response.data) {
            setNotifications(response.data);
          }
        } catch (error) {
          console.error('Error fetching notification preferences:', error);
        } finally {
          setNotificationsLoading(false);
        }
      }
    };

    fetchNotificationPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, landlordId]);

  // Fetch payout settings and history when payouts section is active
  useEffect(() => {
    const fetchPayoutData = async () => {
      if (activeSection === 'payouts' && landlordId) {
        try {
          setPayoutSettingsLoading(true);
          setPayoutHistoryLoading(true);

          // Fetch payout settings, frequency, and history in parallel
          const [settingsResponse, frequencyResponse, historyResponse] = await Promise.all([
            apiService.getPayoutSettings().catch(() => ({ data: null })),
            disbursementService.getPayoutFrequency(landlordId).catch(() => ({ data: null })),
            disbursementService.getPayoutHistory(landlordId, 1, 10).catch(() => ({ success: false, data: null }))
          ]);

          // Update payout settings
          if (settingsResponse.data) {
            setPayoutSettings(prev => ({
              ...prev,
              autoPayout: settingsResponse.data.autoPayout ?? prev.autoPayout,
              minimumAmount: settingsResponse.data.minimumAmount ?? prev.minimumAmount,
              notificationEmail: settingsResponse.data.notificationEmail ?? prev.notificationEmail,
              notificationSMS: settingsResponse.data.notificationSMS ?? prev.notificationSMS
            }));
          }

          // Update payout frequency
          if (frequencyResponse.data?.frequency) {
            setPayoutSettings(prev => ({
              ...prev,
              frequency: (frequencyResponse.data!.frequency.toLowerCase() || 'weekly') as 'daily' | 'weekly' | 'monthly'
            }));
          }

          // Update payout history
          if (historyResponse.success && historyResponse.data) {
            setPayoutHistory(historyResponse.data.payouts || []);
          }
        } catch (error) {
          console.error('Error fetching payout data:', error);
        } finally {
          setPayoutSettingsLoading(false);
          setPayoutHistoryLoading(false);
        }
      }
    };

    fetchPayoutData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, landlordId]);

  // Save notification preferences
  const handleNotificationChange = async (key: string, value: boolean) => {
    const updated = { ...notifications, [key]: value };
    setNotifications(updated);

    try {
      setNotificationsSaving(true);
      await apiService.updateNotificationPreferences(updated);
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      // Revert on error
      setNotifications(notifications);
    } finally {
      setNotificationsSaving(false);
    }
  };

  // Save payout preferences
  const handlePayoutSettingsChange = async (updates: Partial<typeof payoutSettings>) => {
    const updated = { ...payoutSettings, ...updates };
    setPayoutSettings(updated);

    try {
      setPayoutSettingsSaving(true);
      await apiService.updatePayoutSettings({
        autoPayout: updated.autoPayout,
        minimumAmount: updated.minimumAmount,
        notificationEmail: updated.notificationEmail,
        notificationSMS: updated.notificationSMS
      });
    } catch (error) {
      console.error('Error saving payout settings:', error);
      // Revert on error
      setPayoutSettings(payoutSettings);
    } finally {
      setPayoutSettingsSaving(false);
    }
  };

  // Save payout frequency when changed
  const handlePayoutFrequencyChange = async (frequency: 'daily' | 'weekly' | 'monthly') => {
    if (!landlordId) return;

    try {
      setPayoutSettingsSaving(true);
      const response = await disbursementService.updatePayoutFrequency(landlordId, frequency);
      if (response.success) {
        setPayoutSettings(prev => ({ ...prev, frequency }));
      } else {
        console.error('Failed to update payout frequency:', response.error);
        // Revert on error
        const currentResponse = await disbursementService.getPayoutFrequency(landlordId);
        if (currentResponse.success && currentResponse.data) {
          setPayoutSettings(prev => ({
            ...prev,
            frequency: currentResponse.data!.frequency as 'daily' | 'weekly' | 'monthly'
          }));
        }
      }
    } catch (error) {
      console.error('Error updating payout frequency:', error);
    } finally {
      setPayoutSettingsSaving(false);
    }
  };

  // Banking settings state
  const [isEditingBanking, setIsEditingBanking] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [bankingData, setBankingData] = useState({
    bankName: '',
    bankCode: '',
    accountNumber: '',
    accountHolderName: getUserDisplayName(),
  });

  // ── PayHero state ──────────────────────────────────────────────────────────
  const [payheroChannels, setPayheroChannels] = useState<PayHeroChannel[]>([]);
  const [isLoadingPayhero, setIsLoadingPayhero] = useState(false);
  const [isChannelModalOpen, setIsChannelModalOpen] = useState(false);
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [channelFormData, setChannelFormData] = useState<PayHeroChannelData>({
    description: '',
    account_id: '',
    channel_type: 'paybill',
    short_code: '',
    account_number: '',
    reference_mode: 'STATIC',
    reference_template: '',
    propertyOwnerId: null,
    isDefault: false,
  });
  const [isSavingChannel, setIsSavingChannel] = useState(false);
  const [channelFormErrors, setChannelFormErrors] = useState<Record<string, string>>({});
  const [channelModalError, setChannelModalError] = useState<string | null>(null);
  const [channelModalStep, setChannelModalStep] = useState<'details' | 'assign'>('details');
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [assignOnlyChannelId, setAssignOnlyChannelId] = useState<string | null>(null);
  const [payheroError, setPayheroError] = useState<string | null>(null);
  const [payheroSuccess, setPayheroSuccess] = useState<string | null>(null);

  const setPayheroMessage = (type: 'success' | 'error', message: string) => {
    if (type === 'success') {
      setPayheroSuccess(message);
      setPayheroError(null);
      setTimeout(() => setPayheroSuccess(null), 5000);
    } else {
      setPayheroError(message);
      setPayheroSuccess(null);
      setTimeout(() => setPayheroError(null), 8000);
    }
  };

  const EMPTY_CHANNEL_FORM: PayHeroChannelData = {
    description: '', account_id: '', channel_type: 'paybill', short_code: '', account_number: '', reference_mode: 'STATIC', reference_template: '', propertyOwnerId: null, isDefault: false,
  };

  const openAddChannelModal = () => {
    setEditingChannelId(null);
    setAssignOnlyChannelId(null);
    setChannelModalStep('details');
    setSelectedPropertyIds([]);
    setChannelFormData({
      ...EMPTY_CHANNEL_FORM,
      isDefault: payheroChannels.length === 0,
    });
    setChannelFormErrors({});
    setChannelModalError(null);
    setIsChannelModalOpen(true);
  };

  const openEditChannelModal = (ch: PayHeroChannel) => {
    setEditingChannelId(ch.id);
    setAssignOnlyChannelId(null);
    setChannelModalStep('details');
    setSelectedPropertyIds([]);
    setChannelFormData({
      description: ch.description,
      account_id: '',
      channel_type: ch.channelType,
      short_code: ch.shortCode ?? '',
      account_number: ch.accountNumber ?? '',
      reference_mode: ch.referenceMode === 'UNIT_VARIABLE' ? 'UNIT_VARIABLE' : 'STATIC',
      reference_template: ch.referenceTemplate ?? '',
      propertyOwnerId: ch.propertyOwnerId ?? null,
      isDefault: ch.isDefault
    });
    setChannelFormErrors({});
    setChannelModalError(null);
    setIsChannelModalOpen(true);
  };

  const openAssignPropertiesModal = async (ch: PayHeroChannel) => {
    setEditingChannelId(null);
    setAssignOnlyChannelId(ch.id);
    setChannelModalStep('assign');
    setChannelFormData({ ...EMPTY_CHANNEL_FORM, description: ch.description });
    setChannelFormErrors({});
    setChannelModalError(null);
    setIsChannelModalOpen(true);
    const list = await loadAssignProperties();
    setSelectedPropertyIds(
      list.filter((p) => p.payHeroChannelId === ch.id).map((p) => p.id)
    );
  };

  const closeChannelModal = useCallback(() => {
    setIsChannelModalOpen(false);
    setEditingChannelId(null);
    setAssignOnlyChannelId(null);
    setChannelModalStep('details');
    setSelectedPropertyIds([]);
    setChannelFormData({ ...EMPTY_CHANNEL_FORM });
    setChannelFormErrors({});
    setChannelModalError(null);
  }, []);

  useCloseOnRouteChange(isOpen, onClose);
  useCloseOnRouteChange(isChannelModalOpen, closeChannelModal);

  const loadAssignProperties = async () => {
    try {
      setPropertiesLoading(true);
      const response = await apiService.getProperties();
      const propertiesData = response.data?.properties || response.data || [];
      const mapped = (Array.isArray(propertiesData) ? propertiesData : []).map((prop: any) => ({
        id: prop.id,
        name: prop.name,
        totalUnits: prop.totalUnits || 0,
        location: prop.location || '',
        status: 'active',
        payHeroChannelId: prop.payHeroChannelId || prop.payheroChannelId || null,
      }));
      setProperties(mapped);
      return mapped;
    } catch (error) {
      console.error('Error fetching properties for channel assign:', error);
      setProperties([]);
      return [];
    } finally {
      setPropertiesLoading(false);
    }
  };

  const loadPayheroData = async () => {
    if (!landlordId) return;
    setIsLoadingPayhero(true);
    try {
      const chRes = await payheroChannelService.getChannels(landlordId);
      setPayheroChannels(chRes.data || []);
    } catch (err) {
      console.error('Failed to load PayHero channels:', err);
    } finally {
      setIsLoadingPayhero(false);
    }
  };

  const goToAssignStep = async () => {
    const errors = payheroChannelService.validateChannelData(channelFormData);
    if (Object.keys(errors).length > 0) {
      setChannelFormErrors(errors);
      setChannelModalError(null);
      return;
    }
    setChannelFormErrors({});
    setChannelModalError(null);
    setChannelModalStep('assign');
    const list = await loadAssignProperties();
    if (assignOnlyChannelId) {
      setSelectedPropertyIds(
        list.filter((p: { payHeroChannelId?: string | null }) => p.payHeroChannelId === assignOnlyChannelId).map((p: { id: string }) => p.id)
      );
    }
  };

  const togglePropertySelection = (propertyId: string) => {
    setSelectedPropertyIds((prev) =>
      prev.includes(propertyId) ? prev.filter((id) => id !== propertyId) : [...prev, propertyId]
    );
  };

  const saveChannel = async (options?: { skipAssign?: boolean }) => {
    if (!landlordId) {
      setChannelModalError('Unable to save: user session not loaded. Please close and reopen settings.');
      return;
    }

    // Existing channel: assign-only mode
    if (assignOnlyChannelId) {
      setIsSavingChannel(true);
      setChannelModalError(null);
      try {
        const res = await payheroChannelService.assignProperties(
          landlordId,
          assignOnlyChannelId,
          selectedPropertyIds
        );
        if (res.success) {
          const count = (res as any).assigned ?? selectedPropertyIds.length;
          setPayheroMessage('success', `Assigned to ${count} propert${count === 1 ? 'y' : 'ies'}`);
          toast.success('Properties assigned');
          closeChannelModal();
          await loadPayheroData();
        } else {
          setChannelModalError(res.error || 'Failed to assign properties');
        }
      } catch (err) {
        setChannelModalError(err instanceof Error ? err.message : 'Failed to assign properties');
      } finally {
        setIsSavingChannel(false);
      }
      return;
    }

    const errors = payheroChannelService.validateChannelData(channelFormData);
    if (Object.keys(errors).length > 0) {
      setChannelFormErrors(errors);
      setChannelModalError(null);
      setChannelModalStep('details');
      return;
    }
    setChannelFormErrors({});
    setChannelModalError(null);
    setIsSavingChannel(true);
    try {
      let res;
      if (editingChannelId) {
        res = await payheroChannelService.updateChannel(landlordId, editingChannelId, channelFormData);
      } else {
        res = await payheroChannelService.addChannel(landlordId, channelFormData);
        const newId = res.data?.id;
        if (res.success && newId && !options?.skipAssign && selectedPropertyIds.length > 0) {
          await payheroChannelService.assignProperties(landlordId, newId, selectedPropertyIds);
        }
      }
      if (res.success) {
        const assignedNote =
          !editingChannelId && !options?.skipAssign && selectedPropertyIds.length > 0
            ? ` · assigned to ${selectedPropertyIds.length} propert${selectedPropertyIds.length === 1 ? 'y' : 'ies'}`
            : '';
        setPayheroMessage('success', editingChannelId ? 'Channel updated' : `Channel added${assignedNote}`);
        toast.success(editingChannelId ? 'Channel updated' : 'Channel added');
        closeChannelModal();
        await loadPayheroData();
        if (consumeSettingsReturnToAddProperty()) {
          onClose();
          dispatchResumeAddProperty();
        }
      } else {
        setChannelModalError(res.error || 'Failed to save channel');
      }
    } catch (err) {
      setChannelModalError(err instanceof Error ? err.message : 'Failed to save channel');
    } finally {
      setIsSavingChannel(false);
    }
  };

  const deleteChannel = async (channelRecordId: string) => {
    if (!landlordId) return;
    try {
      await payheroChannelService.deleteChannel(landlordId, channelRecordId);
      setPayheroMessage('success', 'Channel removed');
      toast.success('Channel removed');
      await loadPayheroData();
    } catch (err) {
      setPayheroMessage('error', err instanceof Error ? err.message : 'Failed to remove channel');
    }
  };

  const setDefaultChannel = async (channelRecordId: string) => {
    if (!landlordId) return;
    try {
      await payheroChannelService.setDefaultChannel(landlordId, channelRecordId);
      setPayheroMessage('success', 'Default channel updated');
      toast.success('Default channel updated');
      await loadPayheroData();
    } catch (err) {
      setPayheroMessage('error', err instanceof Error ? err.message : 'Failed to set default');
    }
  };
  // ── End PayHero state ──────────────────────────────────────────────────────

  // Update account holder name when user changes
  useEffect(() => {
    if (user) {
      setBankingData(prev => ({
        ...prev,
        accountHolderName: getUserDisplayName()
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Loading state for user fetch
  const [, setIsLoadingUser] = useState(false);

  // API state
  const [bankCodes, setBankCodes] = useState<BankCode[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [isLoadingBankCodes, setIsLoadingBankCodes] = useState(false);
  const [isLoadingBankAccounts, setIsLoadingBankAccounts] = useState(false);
  const [isSavingBankAccount, setIsSavingBankAccount] = useState(false);
  const [bankError, setBankError] = useState<string | null>(null);
  const [bankSuccess, setBankSuccess] = useState<string | null>(null);

  // Fetch current user/landlord ID from API
  useEffect(() => {
    const fetchCurrentUser = async () => {
      if (!user || !isOpen) return; // Only fetch when modal is open and user exists

      setIsLoadingUser(true);
      try {
        const response = await apiService.get('/auth/me');
        const data = response.data;
        const actualUser = data?.user || data;

        if (actualUser?.id) {
          // For team members (AGENT/MANAGER etc.), use createdById as the landlordId
          // so channels are scoped to their landlord's account, not their own.
          const effectiveId = actualUser.createdById ?? actualUser.id;
          setLandlordId(effectiveId);
          console.log('✅ Landlord ID fetched:', effectiveId);
          // Update local API user state so displayName/phone can be preferred
          setApiUser({ phone: actualUser.phone, displayName: actualUser.displayName, email: actualUser.email });
        }
      } catch (error: any) {
        console.error('❌ Failed to fetch user:', error);

        // If 401, try calling /api/auth/callback to ensure user exists
        if (error?.response?.status === 401) {
          try {
            console.log('🔄 User not found, calling /api/auth/callback to create user...');
            const callbackResponse = await apiService.post('/auth/callback', {});
            const callbackData = callbackResponse.data;
            const actualCbUser = callbackData?.user || callbackData;
            if (actualCbUser?.id) {
              setLandlordId(actualCbUser.id);
              console.log('✅ User created/found via callback:', actualCbUser.id);
            }
          } catch (cbError) {
            console.error('❌ User callback failed:', cbError);
          }
        }
      } finally {
        setIsLoadingUser(false);
      }
    };

    fetchCurrentUser();
  }, [user, isOpen]);

  // Auto-dismiss messages after 5 seconds
  const setBankMessage = (type: 'success' | 'error', message: string) => {
    if (type === 'success') {
      setBankSuccess(message);
      setBankError(null);
      // Auto-dismiss success message after 5 seconds
      setTimeout(() => {
        setBankSuccess(null);
      }, 5000);
    } else {
      setBankError(message);
      setBankSuccess(null);
      // Auto-dismiss error message after 8 seconds (longer for errors)
      setTimeout(() => {
        setBankError(null);
      }, 8000);
    }
  };

  // Load PayHero channels when the Payment Channels tab is opened
  useEffect(() => {
    if (isOpen && activeSection === 'payment-channels' && landlordId) {
      loadPayheroData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeSection, landlordId]);

  // API functions
  const loadBankCodes = async () => {
    setIsLoadingBankCodes(true);
    setBankError(null);
    try {
      const response = await landlordBankService.getBankCodes();
      if (response.success && response.data) {
        setBankCodes(response.data);
      } else {
        setBankMessage('error', 'Failed to load bank codes');
      }
    } catch (error) {
      setBankMessage('error', error instanceof Error ? error.message : 'Failed to load bank codes');
    } finally {
      setIsLoadingBankCodes(false);
    }
  };

  const loadBankAccounts = async () => {
    if (!landlordId) return;
    setIsLoadingBankAccounts(true);
    setBankError(null);
    try {
      const response = await landlordBankService.getBankAccounts(landlordId);
      if (response.success && response.data) {
        setBankAccounts(response.data);
      } else {
        setBankMessage('error', 'Failed to load bank accounts');
      }
    } catch (error) {
      setBankMessage('error', error instanceof Error ? error.message : 'Failed to load bank accounts');
    } finally {
      setIsLoadingBankAccounts(false);
    }
  };

  const saveBankAccount = async () => {
    setIsSavingBankAccount(true);
    setBankError(null);
    setBankSuccess(null);

    try {
      // Validate data
      const errors = landlordBankService.validateBankAccountData(bankingData);
      if (errors.length > 0) {
        setBankMessage('error', errors.join(', '));
        return;
      }

      if (!landlordId) {
        setBankMessage('error', 'User ID not available. Please refresh the page.');
        return;
      }

      let response;
      if (editingAccountId) {
        // Update existing account
        response = await landlordBankService.updateBankAccount(landlordId, editingAccountId, bankingData);
        if (response.success && response.data) {
          setBankMessage('success', 'Bank account updated successfully');
        } else {
          const errorMessage = response.error || 'Failed to update bank account';
          const details = response.details ? `\n\nDetails: ${response.details.join(', ')}` : '';
          const warnings = response.warnings ? `\n\nWarnings: ${response.warnings.join(', ')}` : '';
          setBankMessage('error', errorMessage + details + warnings);
        }
      } else {
        // Create new account
        response = await landlordBankService.addBankAccount(landlordId, bankingData);
        if (response.success && response.data) {
          setBankMessage('success', 'Bank account added successfully');
        } else {
          const errorMessage = response.error || 'Failed to add bank account';
          const details = response.details ? `\n\nDetails: ${response.details.join(', ')}` : '';
          const warnings = response.warnings ? `\n\nWarnings: ${response.warnings.join(', ')}` : '';
          setBankMessage('error', errorMessage + details + warnings);
        }
      }

      if (response.success) {
        setIsEditingBanking(false);
        setEditingAccountId(null);
        // Reset form
        setBankingData({
          bankName: '',
          bankCode: '',
          accountNumber: '',
          accountHolderName: '',
        });
        // Reload bank accounts
        await loadBankAccounts();
      }
    } catch (error) {
      setBankMessage('error', error instanceof Error ? error.message : 'Failed to save bank account');
    } finally {
      setIsSavingBankAccount(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updateBankAccount = async () => {
    setIsSavingBankAccount(true);
    setBankError(null);
    setBankSuccess(null);

    try {
      // Validate data
      const errors = landlordBankService.validateBankAccountData(bankingData);
      if (errors.length > 0) {
        setBankMessage('error', errors.join(', '));
        return;
      }

      if (!landlordId) {
        setBankMessage('error', 'User ID not available. Please refresh the page.');
        return;
      }

      // Update bank account
      const response = await landlordBankService.updateBankAccount(
        landlordId,
        editingAccountId || landlordId, // Using landlord ID as account ID if editingAccountId is not set
        bankingData
      );
      if (response.success && response.data) {
        setBankMessage('success', 'Bank account updated successfully');
        setIsEditingBanking(false);
        // Reload bank accounts
        await loadBankAccounts();
      } else {
        setBankMessage('error', response.error || 'Failed to update bank account');
      }
    } catch (error) {
      setBankMessage('error', error instanceof Error ? error.message : 'Failed to update bank account');
    } finally {
      setIsSavingBankAccount(false);
    }
  };

  const deleteBankAccount = async (accountId: string) => {
    setBankAccountToDelete(accountId);
    setShowDeleteBankConfirm(true);
  };

  const handleConfirmDeleteBankAccount = async () => {
    if (!bankAccountToDelete) return;

    setBankError(null);
    setBankSuccess(null);

    try {
      if (!landlordId) {
        setBankMessage('error', 'User ID not available. Please refresh the page.');
        toast.error('Error', {
          description: 'User ID not available. Please refresh the page.',
        });
        return;
      }

      const response = await landlordBankService.deleteBankAccount(landlordId, bankAccountToDelete);
      if (response.success) {
        setBankMessage('success', 'Bank account deleted successfully');
        toast.success('Bank account deleted', {
          description: 'The bank account has been successfully removed.',
        });
        // Reload bank accounts
        await loadBankAccounts();
      } else {
        const errorMsg = response.error || 'Failed to delete bank account';
        setBankMessage('error', errorMsg);
        toast.error('Failed to delete bank account', {
          description: errorMsg,
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete bank account';
      setBankMessage('error', errorMsg);
      toast.error('Failed to delete bank account', {
        description: errorMsg,
      });
    } finally {
      setShowDeleteBankConfirm(false);
      setBankAccountToDelete(null);
    }
  };

  const setAsPrimary = async (accountId: string) => {
    setBankAccountToSetPrimary(accountId);
    setShowSetPrimaryConfirm(true);
  };

  const handleConfirmSetPrimary = async () => {
    if (!bankAccountToSetPrimary) return;

    setBankError(null);
    setBankSuccess(null);

    try {
      // First, get the current account details
      const currentAccount = bankAccounts.find(acc => acc.id === bankAccountToSetPrimary);
      if (!currentAccount) {
        setBankMessage('error', 'Account not found');
        toast.error('Account not found', {
          description: 'The selected bank account could not be found.',
        });
        return;
      }

      // Update the account to be primary (only send isPrimary field to avoid validation issues with masked data)
      if (!landlordId) {
        setBankMessage('error', 'User ID not available. Please refresh the page.');
        return;
      }

      const response = await landlordBankService.updateBankAccount(landlordId, bankAccountToSetPrimary, {
        isPrimary: true
      });

      if (response.success) {
        setBankMessage('success', 'Account set as primary successfully');
        toast.success('Account set as primary', {
          description: 'This account is now the default for payouts.',
        });
        // Reload bank accounts to reflect the changes
        await loadBankAccounts();
      } else {
        const errorMsg = response.error || 'Failed to set account as primary';
        setBankMessage('error', errorMsg);
        toast.error('Failed to set primary account', {
          description: errorMsg,
        });
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to set account as primary';
      setBankMessage('error', errorMsg);
      toast.error('Failed to set primary account', {
        description: errorMsg,
      });
    } finally {
      setShowSetPrimaryConfirm(false);
      setBankAccountToSetPrimary(null);
    }
  };

  // Payout settings state
  const [payoutSettings, setPayoutSettings] = useState({
    frequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
    minimumAmount: 1000,
    autoPayout: true, // Default: enabled (automatic payouts)
    notificationEmail: true,
    notificationSMS: false,
  });
  const [payoutHistory, setPayoutHistory] = useState<PayoutHistory[]>([]);
  const [payoutHistoryLoading, setPayoutHistoryLoading] = useState(false);
  const [payoutSettingsLoading, setPayoutSettingsLoading] = useState(false);
  const [payoutSettingsSaving, setPayoutSettingsSaving] = useState(false);

  // Properties state
  const [properties, setProperties] = useState<Array<{
    id: string;
    name: string;
    totalUnits: number;
    location: string;
    status?: string;
    payHeroChannelId?: string | null;
  }>>([]);
  const [propertiesLoading, setPropertiesLoading] = useState(false);

  // Activity log state
  const [activityLogs, setActivityLogs] = useState<Array<{
    id: string;
    action: string;
    description: string;
    category: string;
    severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
    createdAt: string;
    resource?: string;
    resourceId?: string;
    details?: any;
    actorId?: string;
    actorEmail?: string;
    actorRole?: string;
  }>>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);

  // Notification preferences loading/saving
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsSaving, setNotificationsSaving] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    push: true,
    rentCollection: true,
    payoutNotifications: true,
    maintenanceUpdates: true,
    tenantUpdates: true,
    systemAlerts: true,
  });

  // Security state
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    sessionTimeout: 30,
    loginAlerts: true,
    suspiciousActivityAlerts: true,
    requireStrongPassword: true,
  });
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const CURRENCY_OPTIONS = [
    { value: 'KES', label: 'KES (Kenyan Shilling)' },
    { value: 'USD', label: 'USD (US Dollar)' },
    { value: 'GBP', label: 'GBP (British Pound)' },
    { value: 'EUR', label: 'EUR (Euro)' },
  ];

  const COUNTRY_OPTIONS = [
    { value: 'KE', label: 'Kenya' },
    { value: 'UG', label: 'Uganda' },
    { value: 'TZ', label: 'Tanzania' },
    { value: 'RW', label: 'Rwanda' },
    { value: 'ET', label: 'Ethiopia' },
    { value: 'NG', label: 'Nigeria' },
    { value: 'ZA', label: 'South Africa' },
  ];

  const saveOrganizationProfile = async () => {
    if (!user) return;
    if (!orgForm.name.trim()) {
      setOrgError('Organization name is required');
      return;
    }
    setOrgSaving(true);
    setOrgError(null);
    setOrgSuccess(false);
    try {
      const updated = await organizationService.updateProfile({
        name: orgForm.name.trim(),
        type: orgForm.type,
        email: orgForm.email.trim() || null,
        phone: orgForm.phone.trim() || null,
        website: orgForm.website.trim() || null,
        primaryCity: orgForm.primaryCity.trim() || null,
      });
      setOrgMeta({
        id: updated.id,
        slug: updated.slug,
        status: updated.status,
        onboardingCompletedAt: updated.onboardingCompletedAt ?? null,
      });
      const mappedType =
        orgForm.type === 'PROPERTY_MANAGEMENT_FIRM' ? 'MANAGEMENT_FIRM' : 'LANDLORD';
      try {
        await apiService.put('/landlord-settings/company-details', {
          companyName: orgForm.name.trim(),
          currency,
          accountType: mappedType,
          country,
        });
        setCompanyName(orgForm.name.trim());
        setAccountType(mappedType);
      } catch {
        /* non-fatal — org saved */
      }
      setOrgSuccess(true);
      toast.success('Organization updated');
      setTimeout(() => setOrgSuccess(false), 3000);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to save organization';
      setOrgError(msg);
      toast.error('Failed to save organization', { description: msg });
    } finally {
      setOrgSaving(false);
    }
  };

  const EMPTY_OWNER_FORM = {
    name: '',
    type: 'PERSON' as PropertyOwnerType,
    email: '',
    phone: '',
    notes: '',
    isDefault: false,
  };

  const closeOwnerForm = () => {
    setOwnerFormMode('closed');
    setEditingOwnerId(null);
    setOwnerForm(EMPTY_OWNER_FORM);
    setOwnerError(null);
  };

  const openCreateOwnerForm = () => {
    setEditingOwnerId(null);
    setOwnerForm(EMPTY_OWNER_FORM);
    setOwnerError(null);
    setOwnerFormMode('create');
  };

  const openEditOwnerForm = (owner: PropertyOwner) => {
    setEditingOwnerId(owner.id);
    setOwnerForm({
      name: owner.name || '',
      type: owner.type === 'COMPANY' ? 'COMPANY' : 'PERSON',
      email: owner.email || '',
      phone: owner.phone || '',
      notes: owner.notes || '',
      isDefault: !!owner.isDefault,
    });
    setOwnerError(null);
    setOwnerFormMode('edit');
  };

  const refreshPropertyOwners = async () => {
    const owners = await organizationService.listPropertyOwners();
    setPropertyOwners(owners);
  };

  const savePropertyOwner = async () => {
    if (!ownerForm.name.trim()) {
      setOwnerError('Owner name is required');
      return;
    }
    setOwnerSaving(true);
    setOwnerError(null);
    try {
      if (ownerFormMode === 'edit' && editingOwnerId) {
        await organizationService.updatePropertyOwner(editingOwnerId, {
          name: ownerForm.name.trim(),
          type: ownerForm.type,
          email: ownerForm.email.trim() || null,
          phone: ownerForm.phone.trim() || null,
          notes: ownerForm.notes.trim() || null,
          isDefault: ownerForm.isDefault,
        });
        toast.success('Property owner updated');
      } else {
        await organizationService.createPropertyOwner({
          name: ownerForm.name.trim(),
          type: ownerForm.type,
          email: ownerForm.email.trim() || null,
          phone: ownerForm.phone.trim() || null,
          notes: ownerForm.notes.trim() || null,
        });
        toast.success('Property owner added');
      }
      await refreshPropertyOwners();
      closeOwnerForm();
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to save owner';
      setOwnerError(msg);
      toast.error(msg);
    } finally {
      setOwnerSaving(false);
    }
  };

  const confirmDeletePropertyOwner = async () => {
    if (!ownerToDelete) return;
    setOwnerDeleting(true);
    try {
      const result = await organizationService.deletePropertyOwner(ownerToDelete.id);
      await refreshPropertyOwners();
      if (editingOwnerId === ownerToDelete.id) closeOwnerForm();
      const moved = result.propertiesMoved + result.channelsMoved;
      toast.success(
        moved > 0
          ? `Owner removed. ${result.propertiesMoved} propert${result.propertiesMoved === 1 ? 'y' : 'ies'} reassigned.`
          : 'Property owner removed'
      );
      setOwnerToDelete(null);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to remove owner';
      toast.error(msg);
    } finally {
      setOwnerDeleting(false);
    }
  };

  const saveDocumentDefaults = async () => {
    if (!user) return;
    setCompanyDetailsSaving(true);
    setCompanyDetailsError(null);
    setCompanyDetailsSuccess(false);
    try {
      const mappedType =
        orgForm.type === 'PROPERTY_MANAGEMENT_FIRM' ? 'MANAGEMENT_FIRM' : accountType;
      await apiService.put('/landlord-settings/company-details', {
        companyName: (orgForm.name || companyName).trim() || undefined,
        currency,
        accountType: mappedType,
        country,
      });
      setAccountType(mappedType);
      setCompanyDetailsSuccess(true);
      toast.success('Document defaults saved');
      setTimeout(() => setCompanyDetailsSuccess(false), 3000);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to save document defaults.';
      setCompanyDetailsError(msg);
      toast.error('Failed to save document defaults', { description: msg });
    } finally {
      setCompanyDetailsSaving(false);
    }
  };

  const renderAccountContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      <div>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text)', margin: 0 }}>
          Account
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '4px 0 0' }}>
          Your personal profile and the organization that owns this portfolio.
        </p>
      </div>

      <Tabs value={accountTab} onValueChange={(v) => setAccountTab(v as 'profile' | 'organization')}>
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="organization">Organization</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
                Personal information
              </h3>
              <Button onClick={() => setIsEditingProfile(!isEditingProfile)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Edit3 size={16} />
                  {isEditingProfile ? 'Cancel' : 'Edit Profile'}
                </div>
              </Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <ModalInput
                id="firstName"
                label="First Name"
                value={profileData.firstName}
                onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                disabled={!isEditingProfile}
                placeholder="Enter first name"
                required
              />
              <ModalInput
                id="lastName"
                label="Last Name"
                value={profileData.lastName}
                onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                disabled={!isEditingProfile}
                placeholder="Enter last name"
                required
              />
            </div>

            <ModalInput
              id="email"
              label="Email Address"
              type="email"
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
              disabled={!isEditingProfile}
              placeholder="Enter email address"
              required
            />

            <ModalInput
              id="phone"
              label="Phone Number"
              type="tel"
              value={profileData.phone ?? ''}
              onChange={(e) => setProfileData({ ...profileData, phone: formatPhoneKEForInput(e.target.value) })}
              disabled={!isEditingProfile}
              placeholder="254700000000"
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
                Billing snapshot
              </h3>
              {billingLoading ? (
                <SettingsInlineSkeleton />
              ) : billingData ? (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: '16px',
                  padding: '16px',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  background: 'var(--bg-muted)',
                }}>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Units</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>{billingData.totalUnits}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Band</div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                      {billingData.bandLabel || '—'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Est. monthly</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>
                      {billingData.estimate.isCustom
                        ? 'Custom'
                        : billingData.estimate.amount != null
                          ? `KES ${(billingData.estimate.amount as number).toLocaleString()}`
                          : '—'}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '12px 0', color: 'var(--text-muted)', fontSize: '14px' }}>
                  Unable to load billing snapshot. See Billing for full details.
                </div>
              )}
            </div>

            {isEditingProfile && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <Button variant="outline" onClick={() => setIsEditingProfile(false)}>Cancel</Button>
                <Button
                  disabled={profileSaving}
                  onClick={async () => {
                    setProfileSaving(true);
                    try {
                      const displayName = [profileData.firstName, profileData.lastName].filter(Boolean).join(' ').trim() || null;
                      const phoneToSave = typeof profileData.phone === 'string' && profileData.phone.trim() ? profileData.phone.trim() : null;
                      const res = await apiService.put<{ user: { phone?: string | null; displayName?: string | null; email?: string } }>('/landlord-settings/profile', {
                        phone: phoneToSave,
                        displayName
                      });
                      const u = res.data?.user;
                      if (u) {
                        setApiUser({ phone: u.phone ?? null, displayName: u.displayName ?? null, email: u.email ?? '' });
                        const nameParts = (u.displayName || '').trim().split(/\s+/);
                        setProfileData(prev => ({
                          ...prev,
                          phone: (u.phone && u.phone.trim()) ? u.phone.trim() : '',
                          firstName: nameParts[0] ?? '',
                          lastName: nameParts.slice(1).join(' ') ?? '',
                        }));
                      }
                      setIsEditingProfile(false);
                      toast.success('Profile updated.');
                    } catch (err: any) {
                      toast.error(err?.response?.data?.error || 'Failed to save profile.');
                    } finally {
                      setProfileSaving(false);
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Save size={16} />
                    {profileSaving ? 'Saving...' : 'Save Changes'}
                  </div>
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="organization">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Building2 size={18} /> Organization
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '6px 0 0' }}>
                This is your TenantPlus workspace — properties, team, billing, and payment channels belong here.
              </p>
              {orgMeta && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                  <Badge variant="outline">{orgMeta.status}</Badge>
                  <span>slug: {orgMeta.slug}</span>
                  {!orgMeta.onboardingCompletedAt && (
                    <span style={{ color: '#d97706' }}>Onboarding incomplete — finish setup to unlock the portfolio.</span>
                  )}
                </div>
              )}
            </div>

            {orgLoading || companyDetailsLoading ? (
              <SettingsInlineSkeleton />
            ) : (
              <>
                {(orgError || companyDetailsError) && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    color: 'var(--error)',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <AlertTriangle size={16} />
                    {orgError || companyDetailsError}
                  </div>
                )}
                {(orgSuccess || companyDetailsSuccess) && (
                  <div style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    color: 'var(--success)',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                    <Check size={16} />
                    {orgSuccess ? 'Organization saved.' : 'Document defaults saved.'}
                  </div>
                )}

                <div>
                  <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                    Organization type
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {([
                      { value: 'INDIVIDUAL_LANDLORD' as OrganizationType, title: 'Individual landlord', desc: 'You own and manage your properties' },
                      { value: 'PROPERTY_MANAGEMENT_FIRM' as OrganizationType, title: 'Management firm', desc: 'You manage properties for owners' },
                    ]).map((opt) => {
                      const active = orgForm.type === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setOrgForm((prev) => ({ ...prev, type: opt.value }))}
                          style={{
                            textAlign: 'left',
                            padding: '12px',
                            borderRadius: 10,
                            border: '1px solid var(--border)',
                            background: active ? 'var(--bg-muted)' : 'transparent',
                            cursor: 'pointer',
                            outline: 'none',
                            boxShadow: 'none',
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{opt.title}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{opt.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <ModalInput
                  id="orgName"
                  label="Organization name *"
                  value={orgForm.name}
                  onChange={(e) => setOrgForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Green View Properties"
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <ModalInput
                    id="orgCity"
                    label="Primary city"
                    value={orgForm.primaryCity}
                    onChange={(e) => setOrgForm((prev) => ({ ...prev, primaryCity: e.target.value }))}
                    placeholder="Nairobi"
                  />
                  <ModalInput
                    id="orgPhone"
                    label="Business phone"
                    value={orgForm.phone}
                    onChange={(e) => setOrgForm((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="+2547..."
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <ModalInput
                    id="orgEmail"
                    label="Business email"
                    type="email"
                    value={orgForm.email}
                    onChange={(e) => setOrgForm((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="ops@example.com"
                  />
                  <ModalInput
                    id="orgWebsite"
                    label="Website"
                    value={orgForm.website}
                    onChange={(e) => setOrgForm((prev) => ({ ...prev, website: e.target.value }))}
                    placeholder="https://"
                  />
                </div>

                <div style={{
                  borderTop: '1px solid var(--border)',
                  paddingTop: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Invoice & receipt defaults</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                      Currency and country used on documents (still stored on your user profile during migration).
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label htmlFor="currency" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>Currency</label>
                      <Select
                        id="currency"
                        label=""
                        value={currency}
                        onChange={(value) => setCurrency(String(value))}
                        options={CURRENCY_OPTIONS}
                        fullWidth
                        placeholder="Select currency"
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label htmlFor="country" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>Country</label>
                      <Select
                        id="country"
                        label=""
                        value={country}
                        onChange={(value) => setCountry(String(value))}
                        options={COUNTRY_OPTIONS}
                        fullWidth
                        placeholder="Select country"
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
                  <Button variant="outline" onClick={() => void saveDocumentDefaults()} disabled={companyDetailsSaving || orgSaving}>
                    {companyDetailsSaving ? 'Saving…' : 'Save document defaults'}
                  </Button>
                  <Button onClick={() => void saveOrganizationProfile()} disabled={orgSaving || companyDetailsSaving}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Save size={16} />
                      {orgSaving ? 'Saving…' : 'Save organization'}
                    </div>
                  </Button>
                </div>

                <div style={{
                  borderTop: '1px solid var(--border)',
                  paddingTop: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Property owners</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                        Legal owners under this organization. Management firms can add client owners and assign them on each property.
                      </div>
                    </div>
                    {ownerFormMode === 'closed' && (
                      <Button onClick={openCreateOwnerForm} disabled={ownersLoading}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Plus size={16} />
                          Add owner
                        </div>
                      </Button>
                    )}
                  </div>

                  {ownerFormMode !== 'closed' && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12,
                      padding: 14,
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      background: 'var(--bg-muted)',
                    }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                        {ownerFormMode === 'edit' ? 'Edit property owner' : 'New property owner'}
                      </div>
                      {ownerError && (
                        <div style={{ fontSize: 13, color: 'var(--error)' }}>{ownerError}</div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <ModalInput
                          id="poName"
                          label="Owner name *"
                          value={ownerForm.name}
                          onChange={(e) => setOwnerForm((p) => ({ ...p, name: e.target.value }))}
                          placeholder="e.g. Jane Kamau / Acme Ltd"
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <label style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>Owner type</label>
                          <Select
                            id="poType"
                            label=""
                            value={ownerForm.type}
                            onChange={(value) => setOwnerForm((p) => ({ ...p, type: String(value) as PropertyOwnerType }))}
                            options={[
                              { value: 'PERSON', label: 'Person' },
                              { value: 'COMPANY', label: 'Company' },
                            ]}
                            fullWidth
                          />
                        </div>
                        <ModalInput
                          id="poEmail"
                          label="Email"
                          type="email"
                          value={ownerForm.email}
                          onChange={(e) => setOwnerForm((p) => ({ ...p, email: e.target.value }))}
                          placeholder="optional"
                        />
                        <ModalInput
                          id="poPhone"
                          label="Phone"
                          value={ownerForm.phone}
                          onChange={(e) => setOwnerForm((p) => ({ ...p, phone: e.target.value }))}
                          placeholder="optional"
                        />
                      </div>
                      <ModalInput
                        id="poNotes"
                        label="Notes"
                        value={ownerForm.notes}
                        onChange={(e) => setOwnerForm((p) => ({ ...p, notes: e.target.value }))}
                        placeholder="optional"
                      />
                      {ownerFormMode === 'edit' && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Checkbox
                            id="poIsDefault"
                            checked={ownerForm.isDefault}
                            onChange={(checked) => setOwnerForm((p) => ({ ...p, isDefault: checked }))}
                          />
                          <label htmlFor="poIsDefault" style={{ fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>
                            Set as default owner
                          </label>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button variant="outline" onClick={closeOwnerForm} disabled={ownerSaving}>
                          Cancel
                        </Button>
                        <Button onClick={() => void savePropertyOwner()} disabled={ownerSaving}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Save size={16} />
                            {ownerSaving
                              ? 'Saving…'
                              : ownerFormMode === 'edit'
                                ? 'Save changes'
                                : 'Add property owner'}
                          </div>
                        </Button>
                      </div>
                    </div>
                  )}

                  {ownersLoading ? (
                    <SettingsInlineSkeleton />
                  ) : propertyOwners.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                      No owners yet — a default owner is created when you add a property.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {propertyOwners.map((owner) => (
                        <div
                          key={owner.id}
                          style={{
                            padding: '10px 12px',
                            borderRadius: 10,
                            border: '1px solid var(--border)',
                            background: editingOwnerId === owner.id ? 'rgba(255,255,255,0.04)' : 'var(--bg-muted)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 12,
                            alignItems: 'flex-start',
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                              {owner.name}
                              {owner.isDefault ? (
                                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                                  Default
                                </span>
                              ) : null}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                              {owner.type === 'COMPANY' ? 'Company' : 'Person'}
                              {owner.email ? ` · ${owner.email}` : ''}
                              {owner.phone ? ` · ${owner.phone}` : ''}
                              {' · '}
                              {owner._count?.properties ?? 0} propert{(owner._count?.properties ?? 0) === 1 ? 'y' : 'ies'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <Button
                              variant="outline"
                              onClick={() => openEditOwnerForm(owner)}
                              disabled={ownerSaving || ownerDeleting}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Edit3 size={14} />
                                Edit
                              </div>
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => setOwnerToDelete(owner)}
                              disabled={ownerSaving || ownerDeleting || propertyOwners.length <= 1}
                              title={propertyOwners.length <= 1 ? 'Cannot remove the only owner' : 'Remove owner'}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Trash2 size={14} />
                                Remove
                              </div>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );

  const renderBankingContent = () => (
    <>
      {/* ── Add / Edit Channel Modal ── */}
      <Modal
        isOpen={isChannelModalOpen}
        onClose={closeChannelModal}
        title={
          assignOnlyChannelId
            ? `Assign properties · ${channelFormData.description || 'Channel'}`
            : editingChannelId
              ? 'Edit Payment Channel'
              : channelModalStep === 'assign'
                ? 'Assign to properties'
                : 'Add Payment Channel'
        }
        size="sm"
        zIndex={1100}
      >
        <ModalMainContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {!editingChannelId && !assignOnlyChannelId && (
              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: 'var(--muted-foreground)' }}>
                <span style={{ fontWeight: channelModalStep === 'details' ? 700 : 500, color: channelModalStep === 'details' ? 'var(--foreground)' : undefined }}>
                  1. Channel details
                </span>
                <span>→</span>
                <span style={{ fontWeight: channelModalStep === 'assign' ? 700 : 500, color: channelModalStep === 'assign' ? 'var(--foreground)' : undefined }}>
                  2. Assign properties
                </span>
              </div>
            )}

            <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
              {assignOnlyChannelId || channelModalStep === 'assign'
                ? 'Select which properties should collect rent through this channel. You can change this later.'
                : editingChannelId
                  ? 'Update how tenants pay rent through this route.'
                  : 'Connect a till or paybill tenants will use to pay rent.'}
            </p>

            {channelModalError && (
              <div style={{
                padding: '10px 12px',
                borderRadius: '10px',
                background: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.28)',
                color: '#dc2626',
                fontSize: '13px',
                display: 'flex',
                gap: '8px',
                alignItems: 'flex-start',
              }}>
                <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{channelModalError}</span>
              </div>
            )}

            {channelModalStep === 'details' && !assignOnlyChannelId && (
              <>
            <ModalInput
              id="chDesc"
              label="Channel Name *"
              value={channelFormData.description}
              onChange={(e) => {
                setChannelFormData(p => ({ ...p, description: e.target.value }));
                if (channelFormErrors.description) {
                  setChannelFormErrors(prev => { const next = { ...prev }; delete next.description; return next; });
                }
              }}
              placeholder="e.g. Main Paybill, Office Till"
              error={channelFormErrors.description}
            />

            {propertyOwners.length > 0 && (
              <div>
                <label
                  htmlFor="chOwner"
                  style={{ fontSize: '12px', fontWeight: 700, color: 'var(--muted-foreground)', marginBottom: '8px', display: 'block', letterSpacing: '0.06em', textTransform: 'uppercase' }}
                >
                  Legal owner (optional)
                </label>
                <select
                  id="chOwner"
                  value={channelFormData.propertyOwnerId ?? ''}
                  onChange={(e) =>
                    setChannelFormData((p) => ({
                      ...p,
                      propertyOwnerId: e.target.value || null,
                    }))
                  }
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: '1px solid var(--border)',
                    background: 'var(--background)',
                    color: 'var(--foreground)',
                    fontSize: '14px',
                  }}
                >
                  <option value="">Organization default</option>
                  {propertyOwners.map((owner) => (
                    <option key={owner.id} value={owner.id}>
                      {owner.name}{owner.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: 'var(--muted-foreground)', marginBottom: '8px', display: 'block', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Channel Type
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                {[
                  { value: 'paybill', label: 'Paybill', icon: Hash },
                  { value: 'till', label: 'Till', icon: Zap },
                  { value: 'bank', label: 'Bank', icon: Building2 },
                ].map((opt) => {
                  const active = channelFormData.channel_type === opt.value;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setChannelFormData(p => ({
                          ...p,
                          channel_type: opt.value as any,
                          short_code: '',
                          account_number: '',
                          reference_mode: 'STATIC',
                          reference_template: ''
                        }));
                        setChannelFormErrors({});
                      }}
                      style={{
                        border: active ? '1px solid rgba(var(--primary-rgb,34,197,94),0.45)' : '1px solid var(--border)',
                        background: active ? 'rgba(var(--primary-rgb,34,197,94),0.16)' : 'rgba(255,255,255,0.02)',
                        color: active ? 'var(--foreground)' : 'var(--muted-foreground)',
                        borderRadius: '10px',
                        padding: '10px 8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      <Icon size={14} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {channelFormErrors.channel_type && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#dc2626' }}>{channelFormErrors.channel_type}</div>
              )}
            </div>

            {channelFormData.channel_type === 'till' && (
              <ModalInput
                id="chShortCode"
                label="Till Number *"
                type="text"
                value={String(channelFormData.short_code ?? '')}
                onChange={(e) => {
                  setChannelFormData(p => ({ ...p, short_code: e.target.value }));
                  if (channelFormErrors.short_code) {
                    setChannelFormErrors(prev => { const next = { ...prev }; delete next.short_code; return next; });
                  }
                }}
                placeholder="e.g. 123456"
                error={channelFormErrors.short_code}
              />
            )}

            {(channelFormData.channel_type === 'paybill' || channelFormData.channel_type === 'bank') && (
              <>
                <ModalInput
                  id="chShortCode"
                  label={channelFormData.channel_type === 'bank' ? 'Bank/Paybill Number *' : 'Paybill Number *'}
                  type="text"
                  value={String(channelFormData.short_code ?? '')}
                  onChange={(e) => {
                    setChannelFormData(p => ({ ...p, short_code: e.target.value }));
                    if (channelFormErrors.short_code) {
                      setChannelFormErrors(prev => { const next = { ...prev }; delete next.short_code; return next; });
                    }
                  }}
                  placeholder="e.g. 522522"
                  error={channelFormErrors.short_code}
                />
                <ModalInput
                  id="chAccountNumber"
                  label="Account Reference *"
                  value={channelFormData.account_number ?? ''}
                  onChange={(e) => {
                    setChannelFormData(p => ({ ...p, account_number: e.target.value }));
                    if (channelFormErrors.account_number) {
                      setChannelFormErrors(prev => { const next = { ...prev }; delete next.account_number; return next; });
                    }
                  }}
                  placeholder="e.g. HOUSE-A12"
                  error={channelFormErrors.account_number}
                />
              </>
            )}

            {(channelFormData.channel_type === 'paybill' || channelFormData.channel_type === 'bank') && (
              <div style={{ display: 'grid', gap: '10px' }}>
                  <label style={{ fontSize: '13px', color: 'var(--muted-foreground)', fontWeight: 600 }}>
                    Account reference mode
                  </label>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                      <input
                        type="radio"
                        checked={(channelFormData.reference_mode || 'STATIC') === 'STATIC'}
                        onChange={() => setChannelFormData(p => ({ ...p, reference_mode: 'STATIC' }))}
                      />
                      Static account number
                    </label>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                      <input
                        type="radio"
                        checked={channelFormData.reference_mode === 'UNIT_VARIABLE'}
                        onChange={() => setChannelFormData(p => ({ ...p, reference_mode: 'UNIT_VARIABLE', reference_template: p.reference_template || `${p.account_number || ''}{unit}` }))}
                      />
                      Use unit number variable
                    </label>
                  </div>
                  {channelFormData.reference_mode === 'UNIT_VARIABLE' && (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      <ModalInput
                        id="channelReferenceTemplate"
                        label="Reference Template"
                        value={channelFormData.reference_template ?? ''}
                        onChange={(e) => {
                          setChannelFormData(p => ({ ...p, reference_template: e.target.value }));
                          if (channelFormErrors.reference_template) {
                            setChannelFormErrors(prev => { const next = { ...prev }; delete next.reference_template; return next; });
                          }
                        }}
                        placeholder="e.g. 453563#{unit}"
                        error={channelFormErrors.reference_template}
                      />
                      <div style={{ fontSize: '12px', color: 'var(--muted-foreground)' }}>
                        Use <code>{'{unit}'}</code> where tenant unit number should appear. Example preview:{' '}
                        <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>
                          {(channelFormData.reference_template || '').replace(/\{unit\}/g, 'A12') || '—'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
            )}

            <div style={{
              border: '1px solid var(--border)',
              borderRadius: '10px',
              background: 'rgba(255,255,255,0.03)',
              padding: '10px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Checkbox
                id="chIsDefault"
                checked={!!channelFormData.isDefault}
                onChange={(checked) => setChannelFormData(p => ({ ...p, isDefault: checked }))}
              />
              <label htmlFor="chIsDefault" style={{ fontSize: '13px', color: 'var(--foreground)', cursor: 'pointer' }}>
                Set as default payment route
              </label>
            </div>
              </>
            )}

            {(channelModalStep === 'assign' || assignOnlyChannelId) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {propertiesLoading ? (
                  <div style={{ fontSize: '13px', color: 'var(--muted-foreground)' }}>Loading properties…</div>
                ) : properties.length === 0 ? (
                  <div style={{
                    padding: '16px',
                    borderRadius: '10px',
                    border: '1px dashed var(--border)',
                    fontSize: '13px',
                    color: 'var(--muted-foreground)',
                    textAlign: 'center',
                  }}>
                    No properties yet. Create the channel now and assign properties later from property settings.
                  </div>
                ) : (
                  <div style={{
                    maxHeight: '280px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingRight: '2px',
                  }}>
                    {properties.map((property) => {
                      const checked = selectedPropertyIds.includes(property.id);
                      const otherChannel =
                        property.payHeroChannelId &&
                        property.payHeroChannelId !== assignOnlyChannelId
                          ? payheroChannels.find((c) => c.id === property.payHeroChannelId)
                          : null;
                      return (
                        <label
                          key={property.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: checked ? '1px solid rgba(var(--primary-rgb,34,197,94),0.45)' : '1px solid var(--border)',
                            background: checked ? 'rgba(var(--primary-rgb,34,197,94),0.1)' : 'rgba(255,255,255,0.02)',
                            cursor: 'pointer',
                          }}
                        >
                          <Checkbox
                            id={`ch-prop-${property.id}`}
                            checked={checked}
                            onChange={() => togglePropertySelection(property.id)}
                          />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--foreground)' }}>
                              {property.name}
                            </div>
                            <div style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '2px' }}>
                              {property.location || 'No location'} · {property.totalUnits} units
                              {otherChannel ? ` · currently: ${otherChannel.description}` : ''}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {selectedPropertyIds.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--muted-foreground)' }}>
                    {selectedPropertyIds.length} selected
                  </div>
                )}
              </div>
            )}
          </div>
        </ModalMainContent>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '10px',
          padding: '12px 16px',
          borderTop: '1px solid rgba(0,0,0,0.08)',
          background: 'var(--background)',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}>
          {assignOnlyChannelId ? (
            <>
              <Button variant="outline" size="md" onClick={closeChannelModal}>Cancel</Button>
              <Button size="md" onClick={() => void saveChannel()} disabled={isSavingChannel}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Save size={15} />
                  {isSavingChannel ? 'Saving…' : 'Save assignment'}
                </div>
              </Button>
            </>
          ) : editingChannelId ? (
            <>
              <Button variant="outline" size="md" onClick={closeChannelModal}>Cancel</Button>
              <Button size="md" onClick={() => void saveChannel()} disabled={isSavingChannel}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Save size={15} />
                  {isSavingChannel ? 'Saving…' : 'Update Channel'}
                </div>
              </Button>
            </>
          ) : channelModalStep === 'details' ? (
            <>
              <Button variant="outline" size="md" onClick={closeChannelModal}>Cancel</Button>
              <Button size="md" onClick={() => void goToAssignStep()}>
                Next: Assign properties
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="md" onClick={() => setChannelModalStep('details')}>Back</Button>
              <Button
                variant="outline"
                size="md"
                onClick={() => void saveChannel({ skipAssign: true })}
                disabled={isSavingChannel}
              >
                {isSavingChannel ? 'Saving…' : 'Skip & create'}
              </Button>
              <Button size="md" onClick={() => void saveChannel()} disabled={isSavingChannel}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Save size={15} />
                  {isSavingChannel
                    ? 'Saving…'
                    : selectedPropertyIds.length > 0
                      ? `Create & assign (${selectedPropertyIds.length})`
                      : 'Create channel'}
                </div>
              </Button>
            </>
          )}
        </div>
      </Modal>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <div
          style={{
            padding: '18px',
            borderRadius: '14px',
            border: '1px solid var(--border)',
            background: 'rgba(255,255,255,0.04)',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--foreground)', margin: 0 }}>Payment Channels</h2>
              <p style={{ fontSize: '13px', color: 'var(--muted-foreground)', margin: '4px 0 0' }}>
                Connect channels tenants use to pay rent, assign them to properties, and set one default route.
              </p>
            </div>
            <Button onClick={openAddChannelModal}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={15} /> Add Channel
              </div>
            </Button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 240px))',
              justifyContent: 'start',
              gap: '10px',
            }}
          >
            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ fontSize: '11px', color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Active channels</div>
              <div style={{ marginTop: '4px', fontSize: '20px', fontWeight: 700, color: 'var(--foreground)' }}>{payheroChannels.length}</div>
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)' }}>
              <div style={{ fontSize: '11px', color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Default route</div>
              <div style={{ marginTop: '4px', fontSize: '13px', fontWeight: 600, color: 'var(--foreground)' }}>
                {payheroChannels.find(ch => ch.isDefault)?.description || 'Not set'}
              </div>
            </div>
          </div>
        </div>

        {payheroError && (
          <div style={{ padding: '12px 14px', borderRadius: '10px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', color: '#dc2626', fontSize: '13px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><AlertTriangle size={15} />{payheroError}</div>
          </div>
        )}

        {payheroSuccess && (
          <div style={{ padding: '12px 14px', borderRadius: '10px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.3)', color: '#16a34a', fontSize: '13px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Check size={15} />{payheroSuccess}</div>
          </div>
        )}

        {isLoadingPayhero ? (
          <SettingsChannelGridSkeleton />
        ) : payheroChannels.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 280px))',
              justifyContent: 'start',
              gap: '12px',
            }}
          >
            {payheroChannels.map((ch) => (
              <div
                key={ch.id}
                style={{
                  borderRadius: '14px',
                  border: ch.isDefault ? '1.5px solid rgba(var(--primary-rgb,34,197,94),0.45)' : '1px solid var(--border)',
                  background: ch.isDefault ? 'rgba(var(--primary-rgb,34,197,94),0.1)' : 'rgba(255,255,255,0.03)',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Zap size={16} style={{ color: 'var(--primary)' }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--foreground)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ch.description}
                      </div>
                      <div style={{ marginTop: '3px', fontSize: '11px', color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {ch.channelType}
                      </div>
                    </div>
                  </div>
                  {ch.isDefault && <Badge variant="default">Default</Badge>}
                </div>

                <div style={{ display: 'grid', gap: '6px', fontSize: '12px', color: 'var(--muted-foreground)' }}>
                  <div>
                    {ch.shortCode ? (
                      <>
                        {ch.channelType === 'till' ? 'Short code' : 'Paybill'}:{' '}
                        <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>{ch.shortCode}</span>
                      </>
                    ) : 'Short code: —'}
                  </div>
                  <div>
                    Account:{' '}
                    <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>{ch.accountNumber || '—'}</span>
                  </div>
                  <div>
                    Properties:{' '}
                    <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>
                      {ch.assignedPropertyCount ?? 0}
                    </span>
                  </div>
                  {(ch.channelType === 'paybill' || ch.channelType === 'bank') && (
                    <div>
                      Reference mode:{' '}
                      <span style={{ color: 'var(--foreground)', fontWeight: 600 }}>
                        {ch.referenceMode === 'UNIT_VARIABLE' ? 'Unit variable' : 'Static'}
                      </span>
                      {ch.referenceMode === 'UNIT_VARIABLE' && ch.referenceTemplate ? (
                        <span> · {ch.referenceTemplate}</span>
                      ) : null}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => openEditChannelModal(ch)}
                    title="Edit"
                    style={{
                      padding: '7px 10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(var(--primary-rgb,34,197,94),0.45)',
                      background: 'transparent',
                      color: 'var(--foreground)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Edit3 size={13} /> Edit
                  </button>

                  <button
                    onClick={() => void openAssignPropertiesModal(ch)}
                    title="Assign properties"
                    style={{
                      padding: '7px 10px',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      background: 'rgba(255,255,255,0.04)',
                      color: 'var(--foreground)',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Building2 size={13} /> Assign
                  </button>

                  {!ch.isDefault && (
                    <button
                      onClick={() => setDefaultChannel(ch.id)}
                      title="Set as default"
                      style={{
                        padding: '7px 10px',
                        borderRadius: '8px',
                        border: '1px solid rgba(34,197,94,0.45)',
                        background: 'rgba(34,197,94,0.12)',
                        color: 'var(--primary)',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Star size={13} /> Set default
                    </button>
                  )}

                  <button
                    onClick={() => deleteChannel(ch.id)}
                    title="Remove"
                    style={{
                      padding: '7px 10px',
                      borderRadius: '8px',
                      border: '1px solid rgba(239,68,68,0.4)',
                      background: 'rgba(239,68,68,0.12)',
                      color: '#dc2626',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Trash2 size={13} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ padding: '42px 24px', textAlign: 'center', borderRadius: '14px', border: '1px dashed var(--border)', background: 'rgba(255,255,255,0.03)' }}>
            <Zap size={28} style={{ color: 'var(--muted-foreground)', marginBottom: '10px' }} />
            <p style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '600', color: 'var(--foreground)' }}>No channels yet</p>
            <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--muted-foreground)' }}>
              Add your first channel to start collecting tenant payments.
            </p>
            <Button onClick={openAddChannelModal}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Plus size={14} /> Add Channel</div>
            </Button>
          </div>
        )}
      </div>
    </>
  );


  const renderBillingRenewalAction = (options: {
    title: string;
    description: string;
    buttonLabel: string;
    background: string;
    border: string;
  }) => {
    const pendingInvoice = billingInvoices.find((inv) => inv.status !== 'PAID');
    const amountDue = pendingInvoice?.totalAmount ?? billingStatus?.monthlyFee ?? 0;
    const busy = billingRenewalSubmitting || billingRenewalChecking || billingActivating;

    return (
      <div style={{
        padding: '16px',
        borderRadius: '12px',
        background: options.background,
        border: options.border,
        color: 'var(--text)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <AlertTriangle size={20} />
          <span style={{ fontWeight: '600' }}>{options.title}</span>
        </div>
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>{options.description}</p>
        <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-muted)' }}>
          Amount due: KES {Number(amountDue).toLocaleString()}.
        </p>

        <div style={{ marginTop: '12px', display: 'grid', gap: '12px' }}>
          <ModalInput
            id="billing-renewal-phone"
            label="M-Pesa Phone Number"
            type="tel"
            value={billingRenewalPhone}
            onChange={(e) => setBillingRenewalPhone(formatPhoneKEForInput(e.target.value))}
            placeholder="254700000000"
            disabled={busy}
            required
          />

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button onClick={handleBillingRenewal} disabled={busy}>
              {billingRenewalSubmitting ? 'Sending STK Push...' : options.buttonLabel}
            </Button>
            {billingRenewalInvoiceId && (
              <Button
                onClick={() => void syncBillingRenewalStatus(billingRenewalInvoiceId, billingRenewalTransactionId, true)}
                disabled={busy}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                {billingRenewalChecking ? 'Checking...' : 'Check payment status'}
              </Button>
            )}
          </div>

          {billingRenewalMessage && (
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text)' }}>{billingRenewalMessage}</p>
          )}

          {billingRenewalError && (
            <p style={{ margin: 0, fontSize: '13px', color: '#dc2626' }}>{billingRenewalError}</p>
          )}
        </div>
      </div>
    );
  };

  const renderBillingContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
        Billing & Subscription
      </h2>

      {billingStatusLoading ? (
        <SettingsInlineSkeleton />
      ) : billingStatus ? (
        <>
          {!billingStatus.gate.allowed && (billingStatus.gate.reason === 'trial_expired' || billingStatus.gate.reason === 'mandate_pending' || billingStatus.gate.reason === 'canceled') && (
            renderBillingRenewalAction({
              title: 'Renew your subscription',
              description: 'Your trial has ended. Enter your M-Pesa number below to pay the subscription fee via STK push and restore access immediately after verification.',
              buttonLabel: 'Pay via STK Push',
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
            })
          )}

          {!billingStatus.gate.allowed && (billingStatus.gate.reason === 'past_due' || billingStatus.gate.reason === 'suspended') && (
            renderBillingRenewalAction({
              title: 'Payment past due',
              description: 'Your billing is overdue. Pay the outstanding invoice via M-Pesa STK push and your access will resume automatically once payment is verified.',
              buttonLabel: 'Pay outstanding invoice',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
            })
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            padding: '16px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            borderRadius: '12px',
            background: 'var(--bg-muted)',
          }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Plan</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>
                {billingStatus.subscription?.plan?.id ?? 'Starter'} ({billingStatus.subscription?.plan?.pricePerUnit ?? 300} KES/unit)
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Units</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>{billingStatus.subscription?.unitsCount ?? 0}</div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Monthly fee</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>
                {billingStatus.monthlyFee != null ? `KES ${billingStatus.monthlyFee.toLocaleString()}` : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Next billing date</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                {billingStatus.subscription?.nextBillingDate
                  ? new Date(billingStatus.subscription.nextBillingDate).toLocaleDateString()
                  : billingStatus.subscription?.status === 'TRIALING' && billingStatus.subscription?.trialEndsAt
                    ? `Trial ends ${new Date(billingStatus.subscription.trialEndsAt).toLocaleDateString()}`
                    : '—'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Wallet balance</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)' }}>
                KES {Number(billingStatus.walletBalance).toLocaleString()}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '4px' }}>Status</div>
              <Badge variant={billingStatus.gate.allowed ? 'default' : 'outline'}>
                {billingStatus.subscription?.status ?? billingStatus.gate.subscriptionStatus ?? '—'}
              </Badge>
            </div>
          </div>

          <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>Invoices</h3>
          {billingInvoices.length === 0 ? (
            <div style={{ padding: '16px', color: 'var(--text-muted)', fontSize: '14px', border: '1px dashed rgba(0,0,0,0.2)', borderRadius: '12px' }}>
              No invoices yet. Invoices are created when your first billing cycle runs.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {billingInvoices.map((inv) => (
                <div
                  key={inv.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: '12px',
                    background: 'var(--bg)',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                      KES {inv.totalAmount.toLocaleString()} · {inv.unitsCount} units
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Due {new Date(inv.dueDate).toLocaleDateString()}
                      {inv.paidAt && ` · Paid ${new Date(inv.paidAt).toLocaleDateString()}`}
                    </div>
                  </div>
                  <Badge variant={inv.status === 'PAID' ? 'default' : inv.status === 'FAILED' ? 'destructive' : 'outline'}>
                    {inv.status.toLowerCase()}
                  </Badge>
                </div>
              ))}
            </div>
          )}

          <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
            Need to cancel? Contact support. Your access will continue until the end of the current billing period.
          </p>
        </>
      ) : (
        <div style={{ padding: '24px', color: 'var(--text-muted)', fontSize: '14px' }}>
          Unable to load billing. If you just signed up, add properties and refresh.
        </div>
      )}
    </div>
  );

  const renderPayoutsContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
        Payout Settings
      </h2>

      {/* Payout Frequency */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          Payout Frequency
        </h3>

        {payoutSettingsLoading ? (
          <SettingsInlineSkeleton />
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderRadius: '12px',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            background: 'var(--bg)',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
              Frequency
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '20px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              {['daily', 'weekly', 'monthly'].map((frequency) => {
                const isSelected = payoutSettings.frequency === frequency;
                return (
                  <div
                    key={frequency}
                    onClick={() => !payoutSettingsSaving && handlePayoutFrequencyChange(frequency as 'daily' | 'weekly' | 'monthly')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: payoutSettingsSaving ? 'not-allowed' : 'pointer',
                      opacity: payoutSettingsSaving ? 0.6 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handlePayoutFrequencyChange(frequency as 'daily' | 'weekly' | 'monthly')}
                      disabled={payoutSettingsSaving}
                      size="sm"
                    />
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '400',
                      color: 'var(--text)',
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                      userSelect: 'none'
                    }}>
                      {frequency === 'daily' ? 'Daily' :
                        frequency === 'weekly' ? 'Weekly' : 'Monthly'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Payout Preferences */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          Payout Preferences
        </h3>

        <ToggleRow
          title="Automatic Payouts"
          description="Automatically process payouts based on your frequency setting"
          checked={payoutSettings.autoPayout}
          onChange={() => handlePayoutSettingsChange({ autoPayout: !payoutSettings.autoPayout })}
          disabled={payoutSettingsSaving}
        />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(0, 0, 0, 0.08)', background: 'var(--bg)' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Minimum Payout Amount</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Only process payouts above this amount</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: 'var(--text)' }}>KES</span>
            <input
              type="number"
              value={payoutSettings.minimumAmount}
              onChange={(e) => handlePayoutSettingsChange({ minimumAmount: parseInt(e.target.value) || 0 })}
              disabled={payoutSettingsSaving}
              style={{
                width: '100px',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                background: 'var(--bg)',
                color: 'var(--text)',
                fontSize: '14px'
              }}
            />
          </div>
        </div>
      </div>

      {/* Payout Notifications */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          Payout Notifications
        </h3>

        <ToggleRow
          title="Email Notifications"
          description="Get notified via email when payouts are processed"
          checked={payoutSettings.notificationEmail}
          onChange={() => handlePayoutSettingsChange({ notificationEmail: !payoutSettings.notificationEmail })}
          disabled={payoutSettingsSaving}
        />

        <ToggleRow
          title="SMS Notifications"
          description="Get notified via SMS when payouts are processed"
          checked={payoutSettings.notificationSMS}
          onChange={() => handlePayoutSettingsChange({ notificationSMS: !payoutSettings.notificationSMS })}
          disabled={payoutSettingsSaving}
        />
      </div>

      {/* Recent Payouts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          Recent Payouts
        </h3>

        {payoutHistoryLoading ? (
          <SettingsInlineSkeleton />
        ) : payoutHistory.length === 0 ? (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: '14px',
            border: '1px dashed rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            background: 'var(--bg-muted)'
          }}>
            No payout history available
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {payoutHistory.map((payout) => {
              const statusMap: Record<string, string> = {
                'QUEUED': 'pending',
                'SENT': 'processing',
                'CONFIRMED': 'completed',
                'FAILED': 'failed'
              };
              const statusDisplay = statusMap[payout.status] || payout.status.toLowerCase();

              return (
                <div key={payout.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: '12px',
                  background: 'var(--bg)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Wallet size={16} style={{ color: 'var(--text-muted)' }} />
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>
                        KES {Number(payout.amount || 0).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {new Date(payout.created_at).toLocaleDateString()}
                        {payout.batch?.batch_number && ` • Batch ${payout.batch.batch_number}`}
                      </div>
                    </div>
                  </div>
                  <Badge variant={statusDisplay === 'completed' ? 'default' : statusDisplay === 'failed' ? 'destructive' : 'outline'}>
                    {statusDisplay}
                  </Badge>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderPropertiesContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          Properties
        </h2>
        <Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Plus size={16} />
            Add Property
          </div>
        </Button>
      </div>

      {propertiesLoading ? (
        <SettingsInlineSkeleton />
      ) : properties.length === 0 ? (
        <div style={{
          padding: '24px',
          textAlign: 'center',
          color: 'var(--text-muted)',
          fontSize: '14px',
          border: '1px dashed rgba(0, 0, 0, 0.2)',
          borderRadius: '12px',
          background: 'var(--bg-muted)'
        }}>
          No properties found. Add your first property to get started.
        </div>
      ) : (

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {properties.map((property) => (
            <div key={property.id} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '12px',
              background: 'var(--bg)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Building2 size={20} style={{ color: 'var(--text-muted)' }} />
                <div>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)' }}>
                    {property.name}
                  </div>
                  <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                    {property.totalUnits} units • {property.location}
                  </div>
                </div>
                <Badge variant="default">{property.status}</Badge>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button variant="outline" size="sm">
                  <Edit3 size={16} />
                </Button>
                <Button variant="outline" size="sm">
                  <Eye size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderNotificationsContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
        Notifications
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          General Notifications
        </h3>

        {notificationsLoading ? (
          <SettingsInlineSkeleton />
        ) : (
          <>
            <ToggleRow
              title="Email Notifications"
              description="Receive notifications via email"
              checked={notifications.email}
              onChange={() => handleNotificationChange('email', !notifications.email)}
              disabled={notificationsSaving}
            />

            <ToggleRow
              title="SMS Notifications"
              description="Receive notifications via SMS"
              checked={notifications.sms}
              onChange={() => handleNotificationChange('sms', !notifications.sms)}
              disabled={notificationsSaving}
            />

            <ToggleRow
              title="Push Notifications"
              description="Receive push notifications in browser"
              checked={notifications.push}
              onChange={() => handleNotificationChange('push', !notifications.push)}
              disabled={notificationsSaving}
            />
          </>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          Business Notifications
        </h3>

        {notificationsLoading ? (
          <SettingsInlineSkeleton />
        ) : (
          <>
            <ToggleRow
              title="Rent Collection"
              description="Get notified when rent payments are received"
              checked={notifications.rentCollection}
              onChange={() => handleNotificationChange('rentCollection', !notifications.rentCollection)}
              disabled={notificationsSaving}
            />

            <ToggleRow
              title="Payout Notifications"
              description="Get notified when payouts are processed"
              checked={notifications.payoutNotifications}
              onChange={() => handleNotificationChange('payoutNotifications', !notifications.payoutNotifications)}
              disabled={notificationsSaving}
            />

            <ToggleRow
              title="Maintenance Updates"
              description="Get notified about maintenance requests and updates"
              checked={notifications.maintenanceUpdates}
              onChange={() => handleNotificationChange('maintenanceUpdates', !notifications.maintenanceUpdates)}
              disabled={notificationsSaving}
            />

            <ToggleRow
              title="Tenant Updates"
              description="Get notified about tenant activities and changes"
              checked={notifications.tenantUpdates}
              onChange={() => handleNotificationChange('tenantUpdates', !notifications.tenantUpdates)}
              disabled={notificationsSaving}
            />

            <ToggleRow
              title="System Alerts"
              description="Get notified about system issues and important updates"
              checked={notifications.systemAlerts}
              onChange={() => handleNotificationChange('systemAlerts', !notifications.systemAlerts)}
              disabled={notificationsSaving}
            />
          </>
        )}
      </div>
    </div>
  );

  const renderSecurityContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
        Security
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <ToggleRow
          title="Two-Factor Authentication"
          description="Add an extra layer of security to your account"
          checked={securitySettings.twoFactorEnabled}
          onChange={() => setSecuritySettings({
            ...securitySettings,
            twoFactorEnabled: !securitySettings.twoFactorEnabled
          })}
        />

        <ToggleRow
          title="Login Alerts"
          description="Get notified when someone logs into your account"
          checked={securitySettings.loginAlerts}
          onChange={() => setSecuritySettings({
            ...securitySettings,
            loginAlerts: !securitySettings.loginAlerts
          })}
        />

        <ToggleRow
          title="Suspicious Activity Alerts"
          description="Get notified about unusual account activity"
          checked={securitySettings.suspiciousActivityAlerts}
          onChange={() => setSecuritySettings({
            ...securitySettings,
            suspiciousActivityAlerts: !securitySettings.suspiciousActivityAlerts
          })}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px', borderRadius: '12px', border: '1px solid rgba(0, 0, 0, 0.08)', background: 'var(--bg)' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)', marginBottom: '4px' }}>Change Password</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Update your account password</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <ModalInput
              id="currentPassword"
              label="Current Password"
              type="password"
              value={passwordChangeData.currentPassword}
              onChange={(e) => {
                setPasswordChangeData({ ...passwordChangeData, currentPassword: e.target.value });
                setPasswordError(null);
                setPasswordSuccess(false);
              }}
              placeholder="Enter current password"
            />

            <ModalInput
              id="newPassword"
              label="New Password"
              type="password"
              value={passwordChangeData.newPassword}
              onChange={(e) => {
                setPasswordChangeData({ ...passwordChangeData, newPassword: e.target.value });
                setPasswordError(null);
                setPasswordSuccess(false);
              }}
              placeholder="Enter new password (min 8 characters)"
            />

            <ModalInput
              id="confirmPassword"
              label="Confirm New Password"
              type="password"
              value={passwordChangeData.confirmPassword}
              onChange={(e) => {
                setPasswordChangeData({ ...passwordChangeData, confirmPassword: e.target.value });
                setPasswordError(null);
                setPasswordSuccess(false);
              }}
              placeholder="Confirm new password"
            />

            {passwordError && (
              <div style={{ fontSize: '12px', color: '#dc2626', padding: '8px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '6px', border: '1px solid rgba(239,68,68,0.2)' }}>
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div style={{ fontSize: '12px', color: '#16a34a', padding: '8px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '6px', border: '1px solid rgba(16,185,129,0.2)' }}>
                Password changed successfully!
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <Button
                variant="outline"
                onClick={() => {
                  setPasswordChangeData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordError(null);
                  setPasswordSuccess(false);
                }}
                disabled={passwordChanging}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!passwordChangeData.currentPassword || !passwordChangeData.newPassword || !passwordChangeData.confirmPassword) {
                    setPasswordError('All fields are required');
                    return;
                  }

                  if (passwordChangeData.newPassword.length < 8) {
                    setPasswordError('New password must be at least 8 characters long');
                    return;
                  }

                  if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
                    setPasswordError('New passwords do not match');
                    return;
                  }

                  try {
                    setPasswordChanging(true);
                    setPasswordError(null);
                    const response = await apiService.changePassword(
                      passwordChangeData.currentPassword,
                      passwordChangeData.newPassword
                    );

                    if (response.data?.success) {
                      setPasswordSuccess(true);
                      setPasswordChangeData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                      setTimeout(() => setPasswordSuccess(false), 3000);
                    } else {
                      setPasswordError(response.data?.error || 'Failed to change password');
                    }
                  } catch (error: any) {
                    setPasswordError(error.response?.data?.error || 'Failed to change password');
                  } finally {
                    setPasswordChanging(false);
                  }
                }}
                disabled={passwordChanging}
              >
                {passwordChanging ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreferencesContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
        Preferences
      </h2>

      {/* Appearance Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
          Appearance
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Theme</div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Choose your preferred theme</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {['light', 'dark'].map((themeOption) => (
              <button
                key={themeOption}
                onClick={() => setTheme(themeOption as 'light' | 'dark')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  border: 'none',
                  background: theme === themeOption ? 'var(--primary)' : 'var(--bg-muted)',
                  color: theme === themeOption ? 'var(--primary-foreground, #fff)' : 'var(--text)',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease, color 0.2s ease'
                }}
              >
                {themeOption === 'light' ? (
                  <Sun size={18} />
                ) : (
                  <Moon size={18} />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
        Analytics & Reports
      </h2>

      {analyticsData.loading ? (
        <SettingsInlineSkeleton />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div style={{ padding: '16px', border: '1px solid rgba(0, 0, 0, 0.08)', borderRadius: '12px', background: 'var(--bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <TrendingUp size={16} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Monthly Revenue</span>
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text)' }}>
              KES {analyticsData.monthlyRevenue.toLocaleString()}
            </div>
            <div style={{
              fontSize: '12px',
              color: analyticsData.revenueChange >= 0 ? '#10b981' : analyticsData.revenueChange < 0 ? '#ef4444' : 'var(--text-muted)'
            }}>
              {analyticsData.revenueChange !== 0
                ? `${analyticsData.revenueChange >= 0 ? '+' : ''}${analyticsData.revenueChange.toFixed(1)}% from last month`
                : 'No change from last month'
              }
            </div>
          </div>

          <div style={{ padding: '16px', border: '1px solid rgba(0, 0, 0, 0.08)', borderRadius: '12px', background: 'var(--bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Users size={16} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Active Tenants</span>
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text)' }}>
              {analyticsData.activeTenants}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Currently active
            </div>
          </div>

          <div style={{ padding: '16px', border: '1px solid rgba(0, 0, 0, 0.08)', borderRadius: '12px', background: 'var(--bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Building2 size={16} style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text)' }}>Occupancy Rate</span>
            </div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text)' }}>
              {analyticsData.occupancyRate}%
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Property utilization
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px' }}>
        <Button variant="outline">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Download size={16} />
            Export Report
          </div>
        </Button>
        <Button variant="outline">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PieChart size={16} />
            View Detailed Analytics
          </div>
        </Button>
      </div>
    </div>
  );

  const renderActivityContent = () => {
    const getSeverityColor = (severity: string) => {
      switch (severity) {
        case 'ERROR':
        case 'CRITICAL':
          return '#ef4444';
        case 'WARNING':
          return '#f59e0b';
        default:
          return '#10b981';
      }
    };

    const getCategoryIcon = (category: string) => {
      switch (category) {
        case 'PROPERTY':
          return <Building2 size={14} />;
        case 'TENANT':
          return <Users size={14} />;
        case 'PAYMENT':
          return <CreditCard size={14} />;
        case 'AUTHENTICATION':
          return <ShieldCheck size={14} />;
        case 'TEAM':
          return <Users size={14} />;
        case 'LEASE':
          return <FileText size={14} />;
        default:
          return <Activity size={14} />;
      }
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text)', margin: 0 }}>
            Activity Log
          </h2>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Showing your recent activities
          </div>
        </div>

        {activityLogsLoading ? (
          <SettingsSectionSkeleton />
        ) : activityLogs.length === 0 ? (
          <div style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: 'var(--text-muted)',
            fontSize: '14px',
            border: '1px dashed rgba(0, 0, 0, 0.2)',
            borderRadius: '12px',
            background: 'var(--bg-muted)'
          }}>
            <Activity size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
            <div style={{ fontWeight: '500', marginBottom: '8px' }}>No activity logs yet</div>
            <div style={{ fontSize: '12px' }}>
              Your activities will appear here as you use the system
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '600px', overflowY: 'auto' }}>
            {activityLogs.map((activity) => (
              <div key={activity.id} style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '16px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: '12px',
                background: 'var(--bg)',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.15)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.08)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: getSeverityColor(activity.severity),
                  marginTop: '6px',
                  flexShrink: 0
                }} />
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'var(--bg-muted)',
                  color: 'var(--text-muted)',
                  flexShrink: 0
                }}>
                  {getCategoryIcon(activity.category)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'var(--text)',
                    marginBottom: '4px',
                    lineHeight: '1.4'
                  }}>
                    {activity.description}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <span>{new Date(activity.createdAt).toLocaleString()}</span>
                    {activity.actorId && activity.actorEmail && activity.actorRole && activity.actorId !== landlordId && (
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: 'var(--bg-muted)',
                        fontSize: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Users size={10} />
                        {activity.actorRole} ({activity.actorEmail.split('@')[0]})
                      </span>
                    )}
                    {(activity as any).details && Object.keys((activity as any).details).length > 0 && (
                      <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        background: 'var(--bg-muted)',
                        fontSize: '11px'
                      }}>
                        {activity.category}
                      </span>
                    )}
                  </div>
                </div>
                <Badge variant="outline" style={{ flexShrink: 0 }}>
                  {activity.category}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'account':
        return renderAccountContent();
      case 'billing':
        return renderBillingContent();
      case 'payment-channels':
        return renderBankingContent();
      case 'properties':
        return renderPropertiesContent();
      case 'team':
        return <TeamManagement />;
      case 'notifications':
        return renderNotificationsContent();
      case 'security':
        return renderSecurityContent();
      case 'preferences':
        return renderPreferencesContent();
      case 'analytics':
        return renderAnalyticsContent();
      case 'activity':
        return renderActivityContent();
      default:
        return null;
    }
  };

  const currentNav = NAV_GROUPS.flatMap(g => g.items).find(i => i.id === activeSection);
  const SectionIcon = currentNav?.icon ?? SlidersHorizontal;
  const userInitials = getUserDisplayName()
    .split(' ')
    .filter(Boolean)
    .map((w: string) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

  if (!isOpen) return null;

  return (
    <>
      <SOverlay onClick={onClose}>
        <SPanel onClick={e => e.stopPropagation()}>

          {/* ── Sidebar ── */}
          <SSidebar $mobileOpen={sidebarOpen}>
            <SSidebarTop>
              <SBrand>
                <SBrandIcon><SlidersHorizontal size={15} /></SBrandIcon>
                <span style={{ fontSize: '14px', fontWeight: '700', color: 'var(--foreground)' }}>Settings</span>
              </SBrand>
              <SCloseBtn onClick={() => setSidebarOpen(false)} aria-label="Close sidebar menu">
                <X size={15} />
              </SCloseBtn>
            </SSidebarTop>

            <SNavScroll>
              {NAV_GROUPS.map(group => (
                <SNavGroup key={group.label}>
                  <SNavGroupLabel>{group.label}</SNavGroupLabel>
                  {group.items.map(item => {
                    const Icon = item.icon;
                    const active = activeSection === item.id;
                    return (
                      <SNavBtn
                        key={item.id}
                        $active={active}
                        onClick={() => {
                          if (item.id !== activeSection) userNavigatedSectionRef.current = true;
                          setActiveSection(item.id);
                          setSidebarOpen(false);
                        }}
                      >
                        <SNavBtnIcon $active={active}><Icon size={15} /></SNavBtnIcon>
                        {item.label}
                      </SNavBtn>
                    );
                  })}
                </SNavGroup>
              ))}
            </SNavScroll>

            <SSidebarFooter>
              <SAvatar>{userInitials}</SAvatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12.5px', fontWeight: '600', color: 'var(--foreground)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {getUserDisplayName()}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--muted-foreground)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(user as any)?.email ?? ''}
                </div>
              </div>
            </SSidebarFooter>
          </SSidebar>

          {/* ── Mobile overlay ── */}
          <SMobileOverlay $open={sidebarOpen} onClick={() => setSidebarOpen(false)} />

          {/* ── Main content ── */}
          <SMain>
            <SMainHeader>
              <SMobileMenuToggle onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                <Menu size={20} />
              </SMobileMenuToggle>
              <SMainHeaderIcon><SectionIcon size={19} /></SMainHeaderIcon>
              <SMainHeaderBody>
                <SMainHeaderText>
                  <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--foreground)', lineHeight: 1.3 }}>
                    {currentNav?.label || 'Settings'}
                  </h2>
                  <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: 'var(--muted-foreground)' }}>
                    {currentNav?.desc || ''}
                  </p>
                </SMainHeaderText>
              </SMainHeaderBody>
              <SMainCloseBtn type="button" onClick={onClose} aria-label="Close settings">
                <X size={18} />
              </SMainCloseBtn>
            </SMainHeader>

            <SMainScroll>
              {sectionLoading ? <SettingsSectionSkeleton /> : renderContent()}
            </SMainScroll>
          </SMain>

        </SPanel>
      </SOverlay>

      <ConfirmationDialog
        isOpen={showDeleteBankConfirm}
        onCancel={() => {
          setShowDeleteBankConfirm(false);
          setBankAccountToDelete(null);
        }}
        onConfirm={handleConfirmDeleteBankAccount}
        title="Delete Bank Account"
        description="Are you sure you want to delete this bank account? This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />

      <ConfirmationDialog
        isOpen={showSetPrimaryConfirm}
        onCancel={() => {
          setShowSetPrimaryConfirm(false);
          setBankAccountToSetPrimary(null);
        }}
        onConfirm={handleConfirmSetPrimary}
        title="Set as Primary Account"
        description="Are you sure you want to set this account as primary? This will make it the default account for payouts."
        confirmLabel="Set as Primary"
        variant="default"
      />

      <ConfirmationDialog
        isOpen={!!ownerToDelete}
        onCancel={() => {
          if (ownerDeleting) return;
          setOwnerToDelete(null);
        }}
        onConfirm={() => void confirmDeletePropertyOwner()}
        title="Remove property owner"
        description={
          ownerToDelete
            ? `Remove “${ownerToDelete.name}”? Linked properties and payment channels will be reassigned to another owner in this organization.`
            : 'Remove this property owner?'
        }
        confirmLabel={ownerDeleting ? 'Removing…' : 'Remove'}
        variant="danger"
      />
    </>
  );
};

export default LandlordSettingsModal;
